import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import Header from '../components/Header';
import AddressFormDialog from './AddressFormDialog';
import { usePolling } from '../lib/usePolling';
import { compressImage } from '../lib/compressImage';
import { validateResi } from '../lib/resiValidator';
import PayoutBankPanel from '../components/PayoutBankPanel';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Package, Truck, CheckCircle2, Clock, XCircle, ChevronRight, Loader2, ShoppingBag, ArrowLeft, RotateCcw, CreditCard, MessageCircle, AlertTriangle, Upload, Video, Camera, PackageOpen, Banknote, RefreshCw, MapPin, Copy } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Temporary launch flag — automated shipment (Komerce booking, AWB, pickup,
// label download, retry-pickup, etc.) is hidden for v1. Sellers ship manually
// and tap "Mark as Shipped" with optional tracking info. When the influencer
// tier launches, this gets switched to a per-user check (e.g. user.is_influencer)
// rather than a global constant. All Komerce backend code remains intact.
const AUTO_SHIPMENT_ENABLED = false;

const formatPrice = (price) => `Rp ${Number(price).toLocaleString('id-ID')}`;

const RETURN_REASONS = [
  { value: 'wrong_product', label: 'Wrong product sent' },
  { value: 'missing_items', label: 'Missing items' },
  { value: 'damaged_packing', label: 'Product damaged due to poor packing' },
  { value: 'condition_different', label: 'Condition very different from listing' },
  { value: 'package_never_arrived', label: 'Package never arrived' },
];

const SELLER_DISPUTE_REASONS = [
  { value: 'item_worse_condition', label: 'Item returned in worse condition' },
  { value: 'wrong_item_returned', label: 'Wrong/different item sent back' },
  { value: 'empty_package', label: 'Empty or missing package' },
  { value: 'product_used', label: 'Product used/consumed before returning' },
];

const STATUS_CONFIG = {
  pending:   { label: 'Pending Payment', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  paid:      { label: 'Waiting for Seller to Ship', color: 'bg-blue-100 text-blue-800', icon: Package },
  shipped:   { label: 'Shipped - On the Way', color: 'bg-purple-100 text-purple-800', icon: Truck },
  completed: { label: 'Completed', color: 'bg-green-100 text-green-800', icon: CheckCircle2 },
  failed:    { label: 'Failed', color: 'bg-red-100 text-red-800', icon: XCircle },
  expired:   { label: 'Expired', color: 'bg-red-100 text-red-800', icon: XCircle },
  cancelled: { label: 'Cancelled', color: 'bg-gray-100 text-gray-600', icon: XCircle },
  refunded:  { label: 'Refunded', color: 'bg-orange-100 text-orange-800', icon: RotateCcw },
};

const SALES_STATUS_CONFIG = {
  pending:   { label: 'Pending Payment', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  paid:      { label: 'Paid - Waiting for Shipment', color: 'bg-blue-100 text-blue-800', icon: Package },
  shipped:   { label: 'Shipped - On the Way', color: 'bg-purple-100 text-purple-800', icon: Truck },
  completed: { label: 'Completed', color: 'bg-green-100 text-green-800', icon: CheckCircle2 },
  failed:    { label: 'Failed', color: 'bg-red-100 text-red-800', icon: XCircle },
  expired:   { label: 'Expired', color: 'bg-red-100 text-red-800', icon: XCircle },
  cancelled: { label: 'Cancelled', color: 'bg-gray-100 text-gray-600', icon: XCircle },
  refunded:  { label: 'Refunded', color: 'bg-orange-100 text-orange-800', icon: RotateCcw },
};

function StatusBadge({ status, isSale = false, delivered = false, payoutProcessed = false, pickupStatus = null, shippingMethod = null }) {
  const configMap = isSale ? SALES_STATUS_CONFIG : STATUS_CONFIG;
  // Seller-only: once admin has released the money, surface a clearer "Money Transferred"
  // badge that takes precedence over the generic "Completed" state.
  if (isSale && payoutProcessed) {
    const cfg = { label: 'Money Transferred', color: 'bg-emerald-100 text-emerald-800', icon: Banknote };
    const Icon = cfg.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-body font-semibold ${cfg.color}`} data-testid="status-money-transferred">
        <Icon size={12} /> {cfg.label}
      </span>
    );
  }
  // Show "Delivered" when tracking confirms arrival but order not yet completed
  if (status === 'shipped' && delivered) {
    const cfg = { label: 'Delivered', color: 'bg-green-100 text-green-800', icon: CheckCircle2 };
    const Icon = cfg.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-body font-semibold ${cfg.color}`} data-testid={`status-delivered`}>
        <Icon size={12} /> {cfg.label}
      </span>
    );
  }
  // Intermediate state — booking committed but courier hasn't picked up yet.
  // "Shipped" should ONLY appear once the parcel actually leaves the seller's
  // hands (ro_pickup_status flips to picked_up/in_transit via Komerce webhook).
  // Skipped for manual shipments — there's no pickup signal there, so we trust
  // the seller's "mark as shipped" action as the canonical signal.
  const ps = (pickupStatus || '').toLowerCase();
  const isManual = shippingMethod === 'manual';
  const awaitingPickup = !isManual && status === 'shipped' && !delivered && !['picked_up', 'in_transit', 'delivered'].includes(ps);
  if (awaitingPickup) {
    const cfg = { label: 'Awaiting Courier Pickup', color: 'bg-amber-100 text-amber-800', icon: Clock };
    const Icon = cfg.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-body font-semibold ${cfg.color}`} data-testid="status-awaiting-pickup">
        <Icon size={12} /> {cfg.label}
      </span>
    );
  }
  const cfg = configMap[status] || STATUS_CONFIG.pending;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-body font-semibold ${cfg.color}`} data-testid={`status-${status}`}>
      <Icon size={12} /> {cfg.label}
    </span>
  );
}

function OrderCard({ order, isSeller, isAdmin, onShip, onConfirm, onRequestCancel, onPayNow, onMockPay, onMessage, onRequestReturn, onShipReturn, onConfirmReturnReceived, onCancelReturn, onSellerDispute, onDownloadLabel, onCancelShipment }) {
  const navigate = useNavigate();
  const photo = order.photo || (order.items && order.items[0]?.photo);
  const productName = order.product_name || (order.items && order.items[0]?.product_name) || 'Order';
  const brand = order.brand || (order.items && order.items[0]?.brand) || '';
  // Handle old order format
  const status = order.payment_status || order.status || 'pending';
  const displayStatus = status === 'waiting_for_shipment' ? 'paid' : status;

  // Check if buyer previously cancelled a return (no second chances)
  const hasReturnCancelled = Object.keys(order).some(k => k.startsWith('return_cancelled_'));

  // Two-phase buyer flow:
  //   Phase 1 (shipped, no delivered_at): only Confirm Receipt is shown.
  //   Phase 2 (shipped, delivered_at set, within 2 days): only Report a Problem.
  //   Phase 3 (delivered_at >2 days OR completed): no actionable buttons; D+2
  //     auto-complete + admin payout notification has fired in the background.
  const hasDelivered = !!order.delivered_at;
  const latestReturn = order.active_return || {};
  const returnWasRejected = latestReturn.status === 'rejected';

  let returnExpired = false;
  if (hasDelivered) {
    const deliveredAt = new Date(order.delivered_at);
    returnExpired = (Date.now() - deliveredAt.getTime()) > 2 * 24 * 60 * 60 * 1000;
  }

  // Report a Problem appears AFTER the buyer (or auto-receipt) has confirmed
  // delivery, and only inside the 2-day window.
  const showReturnBtn = !isSeller && ['shipped', 'completed'].includes(displayStatus) && hasDelivered && !returnExpired && !order.has_return_request && displayStatus !== 'refunded' && !hasReturnCancelled && !returnWasRejected;

  // Confirm Receipt only appears BEFORE the buyer has confirmed delivery —
  // i.e. while the parcel is in transit / freshly arrived and the buyer
  // hasn't tapped yet.
  const showConfirmBtn = !isSeller && displayStatus === 'shipped' && !hasDelivered && !order.has_return_request;

  // Seller cancel: only for paid orders, not already requested
  const showCancelBtn = isSeller && displayStatus === 'paid' && !order.cancel_requested;

  return (
    <div className={`bg-white rounded-2xl p-4 border ${isSeller && displayStatus === 'paid' ? 'border-blue-200 ring-1 ring-blue-100' : 'border-cream-100/50'}`} data-testid={`order-${order.order_id}`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <div>
            <p className="text-xs font-body text-[#A89F91]">Order #{order.order_id}</p>
            <p className="text-xs font-body text-[#A89F91]">{new Date(order.created_at).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
          </div>
          {isSeller && displayStatus === 'paid' && (
            <span className="bg-red-500 text-white text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full" data-testid={`new-order-tag-${order.order_id}`}>New</span>
          )}
        </div>
        <StatusBadge status={displayStatus} isSale={isSeller} delivered={hasDelivered} payoutProcessed={!!order.payout_processed} pickupStatus={order.ro_pickup_status} shippingMethod={order.shipping_method} />
      </div>

      <div className="flex gap-3 mb-3">
        {photo && (
          <img src={photo} alt="" className="w-16 h-16 rounded-xl object-cover flex-shrink-0 cursor-pointer"
            onClick={() => order.listing_id && navigate(`/listing/${order.listing_id}`)} />
        )}
        <div className="flex-1 min-w-0">
          <p className="text-xs font-body uppercase tracking-[0.15em] text-[#A89F91]">{brand}</p>
          <p className="font-body font-semibold text-[#2C2A29] text-sm truncate">{productName}</p>
          <p className="text-xs font-body text-[#78716C]">Qty: {order.quantity}</p>
        </div>
        <p className="font-heading font-semibold text-[#2C2A29] whitespace-nowrap">{order.amount ? formatPrice(order.amount) : (order.total ? formatPrice(order.total) : '')}</p>
      </div>

      {/* Buyer's courier selection — show to seller on paid orders */}
      {isSeller && displayStatus === 'paid' && order.shipping_option && order.shipping_cost > 0 && (
        <div className="bg-blue-50 rounded-xl px-3 py-2.5 mb-3 space-y-1" data-testid={`buyer-courier-info-${order.order_id}`}>
          <div className="flex items-center justify-between">
            <span className="text-sm font-body text-blue-700 font-medium">Buyer's courier selection:</span>
            <span className="text-sm font-body text-blue-900 font-semibold">{order.shipping_option}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm font-body text-blue-600">Shipping fee paid:</span>
            <span className="text-sm font-body text-blue-900 font-medium">{formatPrice(order.shipping_cost)}</span>
          </div>
        </div>
      )}

      {/* Ship-by countdown (48h from paid_at) — visible to both seller and buyer on paid orders */}
      {displayStatus === 'paid' && order.paid_at && !order.cancel_requested && (() => {
        const paidAt = new Date(order.paid_at);
        const deadline = new Date(paidAt.getTime() + 48 * 60 * 60 * 1000);
        const msLeft = deadline.getTime() - Date.now();
        const overdue = msLeft <= 0;
        const totalMins = Math.max(0, Math.floor(msLeft / 60000));
        const hrs = Math.floor(totalMins / 60);
        const mins = totalMins % 60;
        const timeStr = hrs >= 1 ? `${hrs}h ${mins}m` : `${mins}m`;
        // Color by urgency: red <6h, amber 6-12h, grey >12h
        const urgency = msLeft < 6 * 3600 * 1000 ? 'red' : msLeft < 12 * 3600 * 1000 ? 'amber' : 'grey';
        const colorClasses = overdue
          ? 'bg-red-100 border-red-200 text-red-800'
          : urgency === 'red' ? 'bg-red-50 border-red-100 text-red-700'
          : urgency === 'amber' ? 'bg-amber-50 border-amber-100 text-amber-700'
          : 'bg-cream-50 border-cream-100 text-[#78716C]';
        return (
          <div className={`rounded-xl px-3 py-2 mb-3 text-sm font-body border flex items-center gap-1.5 ${colorClasses}`} data-testid={`ship-by-${order.order_id}`}>
            <Clock size={11} />
            {overdue
              ? (isSeller ? 'Ship-by window passed — awaiting auto-cancellation' : 'Ship-by window passed — awaiting auto-cancellation by system')
              : (isSeller ? `Ship within ${timeStr} · auto-cancels after 48h from payment` : `Seller ships within ${timeStr}`)}
          </div>
        );
      })()}

      {/* Payment-timeout (Expired) — buyer-only persistent banner */}
      {displayStatus === 'expired' && (
        <div className="bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 mb-3 text-sm font-body text-gray-700" data-testid={`payment-expired-banner-${order.order_id}`}>
          <p className="font-semibold mb-0.5">Order expired — payment not received in time</p>
          <p>
            Order is expired due to payment was not made within 1 hour timeframe. Please create a new order.
          </p>
        </div>
      )}

      {/* Ship-by timeout cancellation notice — visible to both sides */}
      {displayStatus === 'cancelled' && order.cancellation_reason === 'seller_ship_timeout' && (
        <div className="bg-red-50 border border-red-100 rounded-xl px-3 py-2 mb-3 text-sm font-body text-red-800" data-testid={`ship-timeout-cancel-${order.order_id}`}>
          <p className="font-semibold mb-0.5">Auto-cancelled: seller did not ship within 48 hours</p>
          <p>
            {isSeller
              ? 'The item has been restocked. Stashmart retains your account in good standing — please ship faster on future orders.'
              : (order.refund_required
                ? 'Your refund is pending — Stashmart admin will transfer it shortly.'
                : 'Your refund has been processed.')}
          </p>
        </div>
      )}

      {/* Seller-requested cancellation — approved by admin */}
      {displayStatus === 'cancelled' && order.cancellation_reason === 'seller_cancel_approved' && (
        <div className={`rounded-xl px-3 py-2 mb-3 text-sm font-body border ${
          order.refund_required ? 'bg-amber-50 border-amber-100 text-amber-800' : 'bg-emerald-50 border-emerald-100 text-emerald-800'
        }`} data-testid={`seller-cancel-approved-${order.order_id}`}>
          <p className="font-semibold mb-0.5">Order cancellation approved</p>
          <p>
            {isSeller
              ? 'Item has been restocked.'
              : (order.refund_required
                ? 'Stashmart admin will DM you for details about the refund.'
                : 'Your refund has been processed.')}
          </p>
        </div>
      )}

      {/* Admin-initiated cancellation — covers /admin/orders/{id}/cancel */}
      {displayStatus === 'cancelled' && order.cancellation_reason === 'admin_cancel' && (
        <div className="bg-amber-50 border border-amber-100 rounded-xl px-3 py-2 mb-3 text-sm font-body text-amber-800" data-testid={`admin-cancel-${order.order_id}`}>
          <p className="font-semibold mb-0.5">Order cancelled by Stashmart admin</p>
          <p>
            {isSeller
              ? 'Item has been restocked.'
              : 'If you already paid, our admin will DM you about the refund.'}
          </p>
        </div>
      )}

      {/* Fallback context for cancelled orders without a known reason — keeps
          buyers from seeing a bare "Cancelled" badge with no explanation. */}
      {displayStatus === 'cancelled'
        && !['seller_ship_timeout', 'seller_cancel_approved', 'admin_cancel'].includes(order.cancellation_reason)
        && (
        <div className="bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 mb-3 text-sm font-body text-gray-700" data-testid={`cancel-generic-${order.order_id}`}>
          <p className="font-semibold mb-0.5">Order cancelled</p>
          <p>This order is no longer active. If you've been charged, contact admin for reconciliation.</p>
        </div>
      )}

      {/* Delivery + Return deadline info */}
      {!isSeller && hasDelivered && displayStatus === 'shipped' && !order.has_return_request && !hasReturnCancelled && !returnWasRejected && (
        (() => {
          const deliveredAt = new Date(order.delivered_at);
          const deadline = new Date(deliveredAt.getTime() + 2 * 24 * 60 * 60 * 1000);
          const hoursLeft = Math.max(0, Math.floor((deadline.getTime() - Date.now()) / (1000 * 60 * 60)));
          const minutesLeft = Math.max(0, Math.floor((deadline.getTime() - Date.now()) / (1000 * 60)) % 60);
          if (hoursLeft <= 0 && minutesLeft <= 0) return null;
          return (
            <div className="bg-blue-50 border border-blue-100 rounded-xl px-3 py-2 mb-3 text-sm font-body text-blue-700" data-testid={`return-deadline-${order.order_id}`}>
              <Clock size={11} className="inline mr-1" />
              {hoursLeft > 0 ? `${hoursLeft}h ${minutesLeft}m` : `${minutesLeft}m`} left to report a problem
            </div>
          );
        })()
      )}

      {/* Tracking info */}
      {order.courier_name && order.tracking_number && (
        <div className="bg-cream-50 rounded-xl px-3 py-2 mb-3 text-sm font-body">
          <span className="text-[#78716C]">Courier: </span>
          <span className="text-[#2C2A29] font-medium">{order.courier_name}</span>
          <span className="text-[#78716C] ml-3">Resi: </span>
          <span className="text-[#2C2A29] font-medium">{order.tracking_number}</span>
          {order.delivered_at && (
            <span className="ml-3 inline-flex items-center gap-1 text-green-700 font-medium">
              <CheckCircle2 size={11} /> Delivered
            </span>
          )}
          {order.shipping_method === 'rajaongkir' && order.ro_pickup_status && !order.delivered_at && (
            <span className="ml-3 inline-flex items-center gap-1 text-blue-700 font-medium" data-testid={`ro-pickup-status-${order.order_id}`}>
              <Truck size={11} /> Pickup: {order.ro_pickup_status.replace(/_/g, ' ')}
            </span>
          )}
        </div>
      )}

      {/* Seller: Ship-to (buyer's delivery address snapshot). Shown while the
          order is paid/shipped so the seller has everything they need to print
          the parcel label off-app. Snapshotted at sale time — buyer's later
          profile address edits do NOT affect in-flight orders. */}
      {isSeller && ['paid', 'shipped'].includes(displayStatus) && order.buyer_address_snapshot && (() => {
        const ba = order.buyer_address_snapshot;
        const lines = [
          ba.full_name,
          ba.phone,
          ba.address_line,
          [ba.subdistrict_label, ba.postal_code].filter(Boolean).join(' '),
          [ba.city, ba.province].filter(Boolean).join(', '),
          ba.country,
        ].filter(Boolean);
        const copyText = [
          ba.full_name && `${ba.full_name} (${ba.phone || 'no phone'})`,
          ba.address_line,
          ba.subdistrict_label,
          [ba.city, ba.province, ba.postal_code].filter(Boolean).join(', '),
          ba.country,
        ].filter(Boolean).join('\n');
        const onCopy = async () => {
          try {
            await navigator.clipboard.writeText(copyText);
            toast.success('Address copied');
          } catch {
            toast.error('Could not copy — please copy manually');
          }
        };
        return (
          <div className="bg-amber-50 border border-amber-200 rounded-xl px-3 py-2.5 mb-3 text-sm font-body" data-testid={`ship-to-${order.order_id}`}>
            <div className="flex items-center justify-between gap-2 mb-1.5">
              <span className="text-xs font-semibold uppercase tracking-wider text-amber-900 flex items-center gap-1.5">
                <MapPin size={12} /> Ship to
              </span>
              <button onClick={onCopy}
                className="text-xs font-body text-amber-700 hover:text-amber-900 inline-flex items-center gap-1 underline-offset-2 hover:underline"
                data-testid={`copy-ship-to-${order.order_id}`}>
                <Copy size={11} /> Copy
              </button>
            </div>
            <p className="text-sm font-body font-medium text-amber-900 leading-snug">{ba.full_name || '—'}{ba.phone && <span className="text-amber-800 font-normal"> · {ba.phone}</span>}</p>
            <p className="text-sm font-body text-amber-900 leading-snug whitespace-pre-line">{ba.address_line || '—'}</p>
            <p className="text-xs font-body text-amber-800 leading-snug">
              {[ba.subdistrict_label, ba.city, ba.province, ba.postal_code].filter(Boolean).join(', ')}
              {ba.country && [ba.subdistrict_label, ba.city, ba.province, ba.postal_code].filter(Boolean).length > 0 ? ` · ${ba.country}` : ba.country}
            </p>
            {ba.is_one_off && (
              <p className="text-xs font-body text-amber-700 italic mt-1">Buyer chose a one-time delivery address for this order.</p>
            )}
          </div>
        );
      })()}

      {/* Seller-only shipping actions (label + retry-pickup) on shipped,
          system-booked orders. Hidden entirely under the manual-shipment
          flow (AUTO_SHIPMENT_ENABLED = false) since sellers ship outside the
          app and just tap "Mark as Shipped". */}
      {AUTO_SHIPMENT_ENABLED && isSeller && order.ro_order_no && displayStatus === 'shipped' && !order.delivered_at && (
        <div className="flex flex-wrap gap-2 mb-3" data-testid={`ro-actions-${order.order_id}`}>
          {/* Retry pickup — show whenever the parcel hasn't been picked up
              yet (covers both explicit `request_failed` AND the silent "stuck
              in scheduled with no AWB" state). Hidden once the courier has
              the parcel (picked_up / in_transit / delivered). */}
          {!['picked_up', 'in_transit', 'delivered'].includes((order.ro_pickup_status || '').toLowerCase()) && (
            <button onClick={() => onRetryPickup(order.order_id)}
              className="inline-flex items-center gap-1.5 bg-amber-50 hover:bg-amber-100 text-amber-800 rounded-full px-3 py-1.5 text-xs font-body font-semibold transition-all"
              title="Re-send the pickup request to the shipping provider. Use this if it's been a while and no courier has shown up."
              data-testid={`retry-pickup-${order.order_id}`}>
              <RefreshCw size={12} /> Retry pickup
            </button>
          )}
          {(() => {
            const hasAwb = !!(order.tracking_number || order.ro_awb_no);
            return hasAwb ? (
              <button onClick={() => onDownloadLabel(order.order_id)}
                className="inline-flex items-center gap-1.5 bg-blue-50 hover:bg-blue-100 text-blue-800 rounded-full px-3 py-1.5 text-xs font-body font-semibold transition-all"
                data-testid={`download-label-${order.order_id}`}>
                <Package size={12} /> Download Shipping Label
              </button>
            ) : (
              <span className="inline-flex items-center gap-1.5 bg-stone-100 text-stone-500 rounded-full px-3 py-1.5 text-xs font-body font-semibold cursor-not-allowed"
                title="The shipping provider hasn't issued the resi number yet. We'll email you once it's ready."
                data-testid={`download-label-pending-${order.order_id}`}>
                <Package size={12} /> Resi pending from provider
              </span>
            );
          })()}
        </div>
      )}
      {/* AWB-pending explainer — only relevant in the auto-shipment flow. */}
      {AUTO_SHIPMENT_ENABLED && isSeller && order.ro_order_no && displayStatus === 'shipped' && !order.delivered_at &&
       !(order.tracking_number || order.ro_awb_no) && (
        <div className="bg-blue-50 border border-blue-100 rounded-xl px-3 py-2 mb-3 text-sm font-body text-blue-900" data-testid={`awb-pending-explainer-${order.order_id}`}>
          <p className="font-semibold mb-0.5">Resi pending from the shipping provider</p>
          <p className="text-xs leading-relaxed">
            Your booking is confirmed, but the shipping provider hasn't issued the AWB/resi number yet.
            This usually takes a few minutes to a few hours.
            <span className="font-semibold"> We'll email you the moment it's ready</span> — at that point the "Download Shipping Label" button will activate and you can print &amp; attach the label.
          </p>
          <p className="text-xs leading-relaxed mt-1.5">
            ⏰ If no pickup happens within 24 hours, please email us at <a href="mailto:hello@stashmart.store" className="underline font-semibold">hello@stashmart.store</a> with your Order ID and we'll follow up with the provider.
          </p>
        </div>
      )}
      {/* If pickup is failed, show the actual Komerce error inline so seller
          knows what went wrong and can correct (e.g., shift pickup window). */}
      {isSeller && order.ro_pickup_status === 'request_failed' && order.ro_pickup_error && (
        <div className="bg-amber-50 border border-amber-100 rounded-xl px-3 py-2 mb-3 text-sm font-body text-amber-800" data-testid={`pickup-error-${order.order_id}`}>
          <span className="font-semibold">Pickup not scheduled:</span> {order.ro_pickup_error}
        </div>
      )}

      {/* Unboxing video reminder — buyers only, paid/shipped orders before delivery */}
      {!isSeller && ['paid', 'shipped'].includes(displayStatus) && !hasDelivered && (
        <div className="bg-[#FFF5F5] border border-[#F4C2C2] rounded-xl px-3 py-2 mb-3 flex items-start gap-2 text-sm font-body text-[#6B1F2B]" data-testid={`unboxing-reminder-${order.order_id}`}>
          <Video size={13} className="flex-shrink-0 mt-0.5" />
          <span>
            <span className="font-semibold">Record a continuous unboxing video</span> when this parcel arrives — it's the only accepted evidence if you need to Report a Problem.
          </span>
        </div>
      )}

      {/* Seller: bank-details panel — shown during the 48h buyer review window
          (status=shipped + delivered_at set) and the auto-completed payout-
          pending window (status=completed + !payout_processed). Replaces the
          old "Admin will DM you" copy with self-service bank input. */}
      {isSeller && displayStatus === 'shipped' && hasDelivered && (
        <PayoutBankPanel variant="review" orderId={order.order_id} />
      )}
      {isSeller && displayStatus === 'completed' && !order.payout_processed && (
        <PayoutBankPanel variant="completed" orderId={order.order_id} />
      )}

      {/* Payout receipt row — sellers only, once admin has released funds */}
      {isSeller && order.payout_processed && order.payout_at && (
        <div className="bg-emerald-50 border border-emerald-100 rounded-xl px-3 py-2 mb-3 flex items-center gap-2 text-sm font-body text-emerald-800" data-testid={`payout-receipt-${order.order_id}`}>
          <Banknote size={12} className="flex-shrink-0" />
          <span className="font-medium">Paid to you</span>
          <span className="text-emerald-600">·</span>
          <span className="text-emerald-700">
            {new Date(order.payout_at).toLocaleString('id-ID', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
          </span>
        </div>
      )}

      {/* Received receipt row — buyers only, once order is completed */}
      {!isSeller && displayStatus === 'completed' && order.completed_at && (
        <div className="bg-emerald-50 border border-emerald-100 rounded-xl px-3 py-2 mb-3 flex items-center gap-2 text-sm font-body text-emerald-800" data-testid={`received-receipt-${order.order_id}`}>
          <CheckCircle2 size={12} className="flex-shrink-0" />
          <span className="font-medium">Received on</span>
          <span className="text-emerald-600">·</span>
          <span className="text-emerald-700">
            {new Date(order.completed_at).toLocaleString('id-ID', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
          </span>
        </div>
      )}

      {/* Refund status badges */}
      {order.refund_requested && displayStatus !== 'refunded' && (
        <div className="bg-amber-50 rounded-xl px-3 py-2 mb-3 text-sm font-body text-amber-700 font-medium" data-testid={`refund-pending-${order.order_id}`}>
          Refund request submitted — under review
        </div>
      )}
      {displayStatus === 'refunded' && (
        <div className="bg-green-50 rounded-xl px-3 py-2 mb-3 text-sm font-body text-green-700 font-medium" data-testid={`refund-approved-${order.order_id}`}>
          {isSeller && order.cancel_requested
            ? 'Cancellation request approved'
            : isSeller
              ? 'Refund issued to buyer'
              : 'Refund approved'}
        </div>
      )}

      {/* Cancel request status badges — hide once the request has reached
          a terminal state (cancelled or refunded by admin). */}
      {order.cancel_requested && !['cancelled', 'refunded'].includes(displayStatus) && (
        <div className="bg-amber-50 rounded-xl px-3 py-2 mb-3 text-sm font-body text-amber-700 font-medium" data-testid={`cancel-pending-${order.order_id}`}>
          {isSeller
            ? 'Cancellation request submitted — awaiting admin review'
            : 'Seller requested to cancel this order — awaiting admin review'}
          {!isSeller && order.cancel_reason && (
            <p className="text-sm font-body font-normal text-amber-600 mt-0.5">Reason: {order.cancel_reason}</p>
          )}
        </div>
      )}

      {/* Return rejected by admin — persistent banner until order completes */}
      {returnWasRejected && ['shipped', 'completed'].includes(displayStatus) && (
        <div className="bg-red-50 border border-red-100 rounded-xl px-3 py-2 mb-3 text-sm font-body text-red-800" data-testid={`return-rejected-${order.order_id}`}>
          <p className="font-semibold mb-0.5 flex items-center gap-1.5">
            <AlertTriangle size={12} /> Return request rejected by admin
          </p>
          {latestReturn.admin_note && (
            <p className="italic">"{latestReturn.admin_note}"</p>
          )}
          <p className="mt-1 text-sm text-red-600">
            {isSeller
              ? 'The buyer\'s return claim was declined. The order will auto-complete normally.'
              : 'You can still confirm receipt below. The order will auto-complete 4 days after delivery.'}
          </p>
        </div>
      )}

      {/* Return request status badges */}
      {order.has_return_request && (() => {
        const ar = order.active_return || {};
        const retStatus = ar.status;
        const retType = ar.return_type;
        const adminNote = ar.admin_note;
        const seller_dispute = ar.seller_dispute;
        const dispute_resolution = ar.dispute_resolution;

        // R+2 (48h) dispute deadline calculation — starts at return_delivered_at
        let disputeWindowText = '';
        let disputeExpired = false;
        if (ar.return_delivered_at && !seller_dispute) {
          const deliveredAt = new Date(ar.return_delivered_at);
          const deadline = new Date(deliveredAt.getTime() + 48 * 60 * 60 * 1000);
          const minsLeft = Math.floor((deadline - Date.now()) / (1000 * 60));
          if (minsLeft <= 0) { disputeExpired = true; disputeWindowText = 'Dispute window closed'; }
          else if (minsLeft >= 60) { disputeWindowText = `${Math.floor(minsLeft / 60)}h ${minsLeft % 60}m left to dispute`; }
          else { disputeWindowText = `${minsLeft}m left to dispute`; }
        }

        return (
          <>
            {/* Top badge row: status chip + (buyer) Cancel Request */}
            <div className="bg-orange-50 border border-orange-100 rounded-xl px-3 py-2 mb-3 flex items-center justify-between" data-testid={`return-active-${order.order_id}`}>
              <span className="text-sm font-body text-orange-700 font-medium flex items-center gap-1.5">
                <PackageOpen size={13} />
                {retStatus === 'pending' && 'Return request under review'}
                {retStatus === 'approved' && retType === 'return_required' && 'Return approved — item to be sent back'}
                {retStatus === 'approved' && retType === 'refund_only' && 'Return approved — refund-only (no item return needed)'}
                {retStatus === 'return_shipped' && (isSeller
                  ? 'Return shipped — please confirm receipt once the parcel arrives. If you don\'t confirm within 7 days, we\'ll mark it received automatically (you\'ll still have 48h to counter-dispute).'
                  : 'Return shipped — awaiting seller to confirm receipt')}
                {retStatus === 'return_received' && !seller_dispute && (isSeller
                  ? 'Return received — raise a counter-dispute within 48 hours if needed, or admin will process the refund.'
                  : 'Return received by seller — awaiting resolution')}
                {retStatus === 'return_received' && seller_dispute && !dispute_resolution && 'Seller disputed the return — admin reviewing'}
                {retStatus === 'dispute_accepted' && 'Dispute accepted — seller will keep the item price'}
                {retStatus === 'dispute_rejected' && 'Dispute rejected — refund pending for buyer'}
                {retStatus === 'seller_paid' && 'Dispute resolved — seller paid'}
                {!retStatus && 'Return request active'}
              </span>
              {!isSeller && retStatus === 'pending' && (
                <button onClick={() => onCancelReturn(order)}
                  className="text-xs font-body text-orange-500 hover:text-red-600 transition-colors underline underline-offset-2"
                  data-testid={`cancel-return-${order.order_id}`}>
                  Cancel Request
                </button>
              )}
              {/* Confirm Receipt — seller only, when buyer has shipped the
                  return back. Clicking this opens the 48-hour counter-dispute
                  window (status flips return_shipped → return_received). */}
              {isSeller && retStatus === 'return_shipped' && retType === 'return_required' && (
                <button onClick={() => onConfirmReturnReceived(order, ar.return_id)}
                  className="text-xs font-body font-medium text-emerald-700 hover:text-emerald-900 transition-colors bg-emerald-50 hover:bg-emerald-100 rounded-full px-3 py-1 flex-shrink-0"
                  data-testid={`confirm-return-received-${order.order_id}`}>
                  Confirm Receipt
                </button>
              )}
              {/* Dispute Return — seller only, ONLY when parcel has arrived back AND it's a return_required flow AND no existing dispute AND within R+2 (48h) window */}
              {isSeller && retStatus === 'return_received' && retType === 'return_required' && !seller_dispute && !disputeExpired && (
                <div className="flex items-center gap-2">
                  {disputeWindowText && (
                    <span className="text-[10px] font-body text-red-500 whitespace-nowrap" data-testid={`dispute-countdown-${order.order_id}`}>
                      {disputeWindowText}
                    </span>
                  )}
                  <button onClick={() => onSellerDispute(order)}
                    className="text-xs font-body font-medium text-red-600 hover:text-red-800 transition-colors bg-red-50 hover:bg-red-100 rounded-full px-3 py-1"
                    data-testid={`dispute-return-${order.order_id}`}>
                    Dispute Return
                  </button>
                </div>
              )}
              {/* Dispute window closed — seller saw the button expire */}
              {isSeller && retStatus === 'return_received' && retType === 'return_required' && !seller_dispute && disputeExpired && (
                <span className="text-[10px] font-body text-[#A89F91] italic" data-testid={`dispute-expired-${order.order_id}`}>
                  Dispute window closed
                </span>
              )}
              {/* Dispute submitted pill — both sides */}
              {seller_dispute && !dispute_resolution && (
                <span className="text-[10px] font-body font-medium text-red-700 bg-red-100 rounded-full px-2 py-0.5" data-testid={`dispute-submitted-${order.order_id}`}>
                  Dispute under admin review
                </span>
              )}
            </div>

            {/* Dispute resolution notice — visible to both after admin decides */}
            {dispute_resolution && (
              <div className={`rounded-xl px-3 py-2 mb-3 text-sm font-body ${
                dispute_resolution.decision === 'accept'
                  ? 'bg-purple-50 border border-purple-100 text-purple-800'
                  : 'bg-emerald-50 border border-emerald-100 text-emerald-800'
              }`} data-testid={`dispute-resolution-${order.order_id}`}>
                <p className="font-semibold mb-0.5">
                  {dispute_resolution.decision === 'accept'
                    ? (isSeller ? 'Your dispute is accepted' : 'Admin accepted the seller\'s dispute')
                    : (isSeller ? 'Your dispute is rejected' : 'Admin rejected the seller\'s dispute')}
                </p>
                <p>
                  {dispute_resolution.decision === 'accept'
                    ? (isSeller ? 'Admin will DM you for details to transfer the item price.' : 'Your refund has been declined.')
                    : (isSeller ? 'The buyer will be refunded the full amount.' : 'Admin will DM you for details to transfer your refund.')}
                </p>
                {dispute_resolution.admin_note && (
                  <p className="mt-1 italic">"{dispute_resolution.admin_note}"</p>
                )}
              </div>
            )}

            {/* Admin's note — asymmetric visibility:
                - return_required → buyer only (shipping instructions)
                - refund_only     → seller only (context on the decision) */}
            {adminNote && retStatus && retStatus !== 'pending' && (
              (!isSeller && retType === 'return_required') ||
              (isSeller && retType === 'refund_only')
            ) && (
              <div className="bg-blue-50 border border-blue-100 rounded-xl px-3 py-2 mb-3 text-sm font-body text-blue-800" data-testid={`admin-note-${order.order_id}`}>
                <span className="font-semibold">Note from admin:</span> {adminNote}
              </div>
            )}

            {/* Buyer callout for refund_only — explicit "no need to ship back" reassurance */}
            {!isSeller && retStatus === 'approved' && retType === 'refund_only' && (
              <div className="bg-emerald-50 border border-emerald-100 rounded-xl px-3 py-2 mb-3 text-sm font-body text-emerald-800" data-testid={`refund-only-notice-${order.order_id}`}>
                You don't have to send back the item. Your refund will be processed shortly.
              </div>
            )}

            {/* Buyer instructions — return approved, must ship item back */}
            {!isSeller && retStatus === 'approved' && retType === 'return_required' && (
              <div className="bg-amber-50 border border-amber-100 rounded-xl px-3 py-2 mb-3" data-testid={`buyer-ship-prompt-${order.order_id}`}>
                <p className="text-sm font-body text-amber-800 mb-2">
                  Please pack the item securely and ship it back to the seller within <strong>2 days</strong>, then enter your courier and tracking number below.
                </p>
                <button
                  onClick={() => onShipReturn(order, ar.return_id)}
                  className="w-full bg-[#F4C2C2] text-[#2C2A29] rounded-full px-4 py-2 text-xs font-body font-semibold hover:bg-[#ECAEAE] transition-all flex items-center justify-center gap-1.5"
                  data-testid={`ship-return-btn-${order.order_id}`}
                >
                  <Truck size={13} /> Enter Courier & Tracking (Ship Back)
                </button>
              </div>
            )}

            {/* Seller's return address — shown to the buyer once admin approves
                a return-required return. Same visual language as the seller's
                "Ship to" panel. Buyer can copy the address with a single tap. */}
            {!isSeller && retType === 'return_required' && ar.seller_return_address && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl px-3 py-2.5 mb-3 text-sm font-body" data-testid={`return-to-${order.order_id}`}>
                <div className="flex items-center justify-between gap-2 mb-1.5">
                  <span className="text-xs font-semibold uppercase tracking-wider text-amber-900 flex items-center gap-1.5">
                    <MapPin size={12} /> Return to (seller's address)
                  </span>
                  <button
                    onClick={async () => {
                      const a = ar.seller_return_address;
                      const txt = [
                        a.full_name && `${a.full_name}${a.phone ? ` (${a.phone})` : ''}`,
                        a.address_line,
                        a.subdistrict_label,
                        [a.city, a.province, a.postal_code].filter(Boolean).join(', '),
                        a.country,
                      ].filter(Boolean).join('\n');
                      try { await navigator.clipboard.writeText(txt); toast.success('Address copied'); }
                      catch { toast.error('Could not copy'); }
                    }}
                    className="text-xs font-body text-amber-700 hover:text-amber-900 inline-flex items-center gap-1 underline-offset-2 hover:underline"
                    data-testid={`copy-return-to-${order.order_id}`}
                  >
                    <Copy size={11} /> Copy
                  </button>
                </div>
                <p className="text-sm font-body font-medium text-amber-900 leading-snug">{ar.seller_return_address.full_name || '—'}{ar.seller_return_address.phone && <span className="text-amber-800 font-normal"> · {ar.seller_return_address.phone}</span>}</p>
                <p className="text-sm font-body text-amber-900 leading-snug">{ar.seller_return_address.address_line || '—'}</p>
                <p className="text-xs font-body text-amber-800 leading-snug">
                  {[ar.seller_return_address.subdistrict_label, ar.seller_return_address.city, ar.seller_return_address.province, ar.seller_return_address.postal_code].filter(Boolean).join(', ')}
                  {ar.seller_return_address.country ? ` · ${ar.seller_return_address.country}` : ''}
                </p>
              </div>
            )}

            {/* Seller instructions — return approved, buyer will ship back */}
            {isSeller && retStatus === 'approved' && retType === 'return_required' && (
              <div className="bg-amber-50 border border-amber-100 rounded-xl px-3 py-2 mb-3 text-sm font-body text-amber-800" data-testid={`seller-return-approved-${order.order_id}`}>
                Admin has approved this return. The buyer will ship the item back to you. You'll see tracking here once they do.
              </div>
            )}

            {/* Return tracking — visible to both once buyer has shipped */}
            {['return_shipped', 'return_received'].includes(retStatus) && (ar.return_courier || ar.return_tracking_number) && (
              <div className="bg-cream-50 rounded-xl px-3 py-2 mb-3 text-sm font-body" data-testid={`return-tracking-${order.order_id}`}>
                <span className="text-[#78716C]">Return courier: </span>
                <span className="text-[#2C2A29] font-medium">{ar.return_courier || '—'}</span>
                <span className="text-[#78716C] ml-3">Tracking: </span>
                <span className="text-[#2C2A29] font-medium">{ar.return_tracking_number || '—'}</span>
                {ar.return_delivered_at && (
                  <span className="ml-3 inline-flex items-center gap-1 text-green-700 font-medium">
                    <CheckCircle2 size={11} /> Delivered to seller
                  </span>
                )}
              </div>
            )}
          </>
        );
      })()}

      {/* Message Button */}
      {['paid', 'shipped', 'completed'].includes(displayStatus) && (
        <button
          onClick={() => onMessage(order, isSeller)}
          className="w-full border border-cream-100 bg-transparent text-[#78716C] rounded-full px-4 py-2 text-xs font-body font-semibold transition-all hover:border-[#F4C2C2] hover:bg-[#FFF5F5] hover:text-[#2C2A29] flex items-center justify-center gap-1.5 mb-2"
          data-testid={`message-order-${order.order_id}`}
        >
          <MessageCircle size={13} /> Message {isSeller ? 'Buyer' : 'Seller'}
        </button>
      )}

      {/* Action Buttons */}
      <div className="flex items-center gap-2 mt-2">
        {/* Buyer: Pay Now — for pending orders */}
        {!isSeller && displayStatus === 'pending' && (() => {
          let expiryText = '';
          let isExpired = false;
          if (order.expires_at) {
            const expiresAt = new Date(order.expires_at);
            const hoursLeft = Math.floor((expiresAt - Date.now()) / (1000 * 60 * 60));
            const minsLeft = Math.floor((expiresAt - Date.now()) / (1000 * 60));
            if (minsLeft <= 0) { isExpired = true; expiryText = 'Cancelling…'; }
            else if (hoursLeft >= 1) { expiryText = `Expires in ${hoursLeft}h`; }
            else { expiryText = `Expires in ${minsLeft}m`; }
          }
          return isExpired ? (
            <span className="text-xs font-body text-gray-500" data-testid={`payment-expired-${order.order_id}`}>Order is being cancelled — refresh in a moment</span>
          ) : (
            <div className="flex flex-col gap-1 flex-1">
              <div className="flex items-center gap-3">
                <button onClick={() => onPayNow(order)}
                  className="flex-1 bg-[#F4C2C2] text-[#2C2A29] rounded-full px-4 py-2.5 text-sm font-body font-semibold hover:bg-[#ECAEAE] transition-all flex items-center justify-center gap-1.5"
                  data-testid={`pay-now-${order.order_id}`}>
                  <CreditCard size={14} /> Pay Now
                </button>
                {/* Mock Pay — admin-only escape hatch (Feb 2026). Previously gated by
                    REACT_APP_SHOW_MOCK_PAY env; now shown only to admins so there's no
                    risk of regular buyers bypassing payment in production. */}
                {isAdmin && (
                  <button onClick={() => onMockPay(order)}
                    className="bg-amber-100 text-amber-800 rounded-full px-3 py-2.5 text-xs font-body font-semibold hover:bg-amber-200 transition-all"
                    data-testid={`mock-pay-${order.order_id}`}>
                    Mock Pay
                  </button>
                )}
                {expiryText && <span className="text-xs font-body text-amber-600 whitespace-nowrap">{expiryText}</span>}
              </div>
            </div>
          );
        })()}

        {/* Buyer: Confirm Receipt — appears while parcel is in transit / freshly
            arrived. Tapping it stamps delivered_at and opens a 2-day Report a
            Problem window. The order does NOT complete instantly — D+2 auto-
            complete + admin payout notification happens server-side. */}
        {showConfirmBtn && (
          <div className="flex-1 flex flex-col items-center">
            <button onClick={() => onConfirm(order.order_id)}
              className="w-full bg-green-100 text-green-800 rounded-full px-4 py-2 text-sm font-body font-semibold hover:bg-green-200 transition-all"
              data-testid={`confirm-received-${order.order_id}`}>
              Confirm Receipt
            </button>
            <span className="text-xs font-body text-[#A89F91] mt-1 text-center">Confirm you have received the item. If there's a problem, you can report it afterwards.</span>
          </div>
        )}

        {/* Seller: Ship order */}
        {isSeller && displayStatus === 'paid' && !order.cancel_requested && (
          <button onClick={() => onShip(order)}
            className="flex-1 bg-blue-100 text-blue-800 rounded-full px-4 py-2 text-sm font-body font-semibold hover:bg-blue-200 transition-all flex items-center justify-center gap-1.5"
            data-testid={`ship-order-${order.order_id}`}>
            <Truck size={14} /> Ship the Product
          </button>
        )}

        {/* Seller: Request cancel — only for paid, not yet requested */}
        {showCancelBtn && (
          <button
            onClick={() => onRequestCancel(order)}
            className="text-xs font-body text-[#A89F91] hover:text-[#D48585] transition-colors underline underline-offset-2"
            data-testid={`request-cancel-${order.order_id}`}
          >
            Request Cancellation
          </button>
        )}

        {/* Buyer: Report a Problem (Return) */}
        {showReturnBtn && !returnExpired && (
          <button
            onClick={() => onRequestReturn(order)}
            className="flex items-center gap-1 text-xs font-body font-medium text-orange-600 hover:text-orange-800 transition-colors bg-orange-50 hover:bg-orange-100 rounded-full px-3 py-1.5 ml-auto"
            data-testid={`request-return-${order.order_id}`}
          >
            <AlertTriangle size={12} /> Report a Problem
          </button>
        )}
        {showReturnBtn && returnExpired && (
          <span className="text-xs font-body text-[#D5CFC7] ml-auto" data-testid={`return-expired-${order.order_id}`}>
            Return window expired (2 days)
          </span>
        )}
      </div>
    </div>
  );
}

export default function OrdersPage() {
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading, fetchOrdersCount, user: currentUser } = useAuth();
  const [tab, setTab] = useState('purchases');
  const [purchases, setPurchases] = useState([]);
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [shipDialog, setShipDialog] = useState(null);
  const [pickupAddr, setPickupAddr] = useState(null);
  // AddressFormDialog (same one used by buyer on /profile + /cart) — we open it
  // when the seller's address is missing / incomplete so they can fill it with
  // phone + street + map pinpoint, saved to db.addresses. Single source of truth.
  const [addrDialogOpen, setAddrDialogOpen] = useState(false);
  const [addrForm, setAddrForm] = useState({
    full_name: '', phone: '', address_line: '', city: '', province: '',
    postal_code: '', country: 'Indonesia', latitude: null, longitude: null,
    subdistrict_id: null, subdistrict_label: '',
  });
  const [bookForm, setBookForm] = useState({ pickup_date: '', pickup_time: '09:00', pickup_vehicle: 'Motor', size_ack: false });
  const [bookSubmitting, setBookSubmitting] = useState(false);
  // Manual shipment form — sellers enter the courier they actually used + the
  // tracking number (resi/AWB). Active while AUTO_SHIPMENT_ENABLED = false.
  const [manualShipForm, setManualShipForm] = useState({ courier_name: '', tracking_number: '', shipment_proof_url: '', awb_format_overridden: false });
  const [manualShipSubmitting, setManualShipSubmitting] = useState(false);
  const [proofUploading, setProofUploading] = useState(false);
  // Live AWB validation — runs on every keystroke so the seller gets instant
  // feedback. Submit button stays disabled while hardError !== null UNLESS the
  // error is a pattern mismatch AND the seller has ticked the escape-hatch
  // checkbox confirming the AWB is correct as printed.
  const manualShipResi = validateResi(manualShipForm.courier_name, manualShipForm.tracking_number);
  const manualShipTrackingTouched = manualShipForm.tracking_number.length > 0;
  // Auto-reset the override flag if the seller fixes the AWB so it now
  // passes validation — prevents a stale override sneaking onto a clean order.
  useEffect(() => {
    if (manualShipForm.awb_format_overridden && !manualShipResi.hardError) {
      setManualShipForm(f => ({ ...f, awb_format_overridden: false }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manualShipResi.hardError]);
  const manualShipBlocked = manualShipResi.hardError && !(
    manualShipResi.errorKind === 'pattern' && manualShipForm.awb_format_overridden
  );
  const [submitting, setSubmitting] = useState(false);
  const [cancelDialog, setCancelDialog] = useState({ open: false, order: null });
  // changePaymentDialog state removed — see comment in handlePayNow region.
  const [cancelReason, setCancelReason] = useState('');
  const [returnDialog, setReturnDialog] = useState({ open: false, order: null });
  const [returnForm, setReturnForm] = useState({ listing_id: '', reason: '', reason_detail: '', video_url: '', photo_urls: [] });
  const [returnUploading, setReturnUploading] = useState(false);
  const [returnShipDialog, setReturnShipDialog] = useState({ open: false, order: null, returnId: '' });
  const [returnShipForm, setReturnShipForm] = useState({ return_courier: '', return_tracking_number: '', return_shipment_proof_url: '', return_awb_format_overridden: false });
  const [returnShipProofUploading, setReturnShipProofUploading] = useState(false);
  // Live AWB validation for the return-ship leg — same pipeline as the
  // outbound seller-ship form so the buyer can't enter nonsense AWBs and walk
  // away (return-leg fraud risk is HIGHER because their refund is already
  // approved).
  const returnShipResi = validateResi(returnShipForm.return_courier, returnShipForm.return_tracking_number);
  const returnShipTrackingTouched = returnShipForm.return_tracking_number.length > 0;
  useEffect(() => {
    if (returnShipForm.return_awb_format_overridden && !returnShipResi.hardError) {
      setReturnShipForm(f => ({ ...f, return_awb_format_overridden: false }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [returnShipResi.hardError]);
  const returnShipBlocked = returnShipResi.hardError && !(
    returnShipResi.errorKind === 'pattern' && returnShipForm.return_awb_format_overridden
  );
  const [sellerDisputeDialog, setSellerDisputeDialog] = useState({ open: false, order: null, returnId: '' });
  const [sellerDisputeForm, setSellerDisputeForm] = useState({ reason: '', reason_detail: '', photo_urls: [] });

  // Handle ?payment=success query param
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('payment') === 'success') {
      toast.success('Payment submitted! Your order will be updated once payment is confirmed.');
      // Friendly reminder for buyers — the unboxing video is the only accepted
      // evidence for seller-fault returns, so flag it as early as possible.
      setTimeout(() => {
        toast('Reminder: Record a continuous unboxing video when your parcel arrives. It\'s required if you ever need to Report a Problem.', {
          duration: 9000,
          icon: '📹',
        });
      }, 900);
      // Clean the URL and clear payment redirect flag
      sessionStorage.removeItem('payment_redirect');
      window.history.replaceState({}, '', window.location.pathname);
    } else if (params.get('payment') === 'cancelled') {
      toast.info('Payment was cancelled. You can retry from your orders.');
      sessionStorage.removeItem('payment_redirect');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  useEffect(() => {
    // Wait for auth check to finish before deciding
    if (authLoading) return;
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }
    setLoading(true);
    Promise.all([
      axios.get(`${API}/orders/my-purchases`).catch(() => ({ data: [] })),
      axios.get(`${API}/orders/my-sales`).catch(() => ({ data: [] })),
    ]).then(([purchRes, salesRes]) => {
      setPurchases(purchRes.data);
      setSales(salesRes.data);
    }).finally(() => setLoading(false));
  }, [isAuthenticated, authLoading]);

  // Auto-poll orders every 30 seconds for status updates
  // Background refresh while the Orders page is open. Initial fetch happens in
  // another effect above; this one only drives periodic + focus-based refetches.
  const fetchOrdersSilently = useCallback(() => {
    if (!isAuthenticated || authLoading) return;
    Promise.all([
      axios.get(`${API}/orders/my-purchases`).catch(() => ({ data: [] })),
      axios.get(`${API}/orders/my-sales`).catch(() => ({ data: [] })),
    ]).then(([purchRes, salesRes]) => {
      setPurchases(purchRes.data);
      setSales(salesRes.data);
    });
  }, [isAuthenticated, authLoading]);
  usePolling(fetchOrdersSilently, 20000, isAuthenticated && !authLoading);

  const handleConfirmReceived = async (orderId) => {
    try {
      const { data } = await axios.post(`${API}/orders/${orderId}/confirm-received`, {});
      setPurchases(prev => prev.map(o => o.order_id === orderId ? {
        ...o,
        // Order stays in `shipped` status — only delivered_at is stamped so
        // the 2-day Report-a-Problem window opens. Background task flips to
        // `completed` after D+2.
        delivered_at: data.delivered_at || new Date().toISOString(),
        delivered_confirmed_by_buyer: true,
      } : o));
      fetchOrdersCount();
      toast.success("Receipt confirmed. If anything's wrong, you have 2 days to report a problem.");
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to confirm');
    }
  };

  // Compute "today" in Asia/Jakarta (UTC+7) so the date picker matches what
  // Komerce expects regardless of where the seller's browser thinks it is.
  const jakartaTodayISO = () => {
    const now = new Date();
    const jakartaMs = now.getTime() + (now.getTimezoneOffset() * 60000) + (7 * 60 * 60 * 1000);
    return new Date(jakartaMs).toISOString().slice(0, 10);
  };

  // Default pickup_date to tomorrow in Jakarta time (Komerce requires
  // pickup_date >= today, with min 90 min lead time).
  const tomorrowISO = () => {
    const now = new Date();
    const jakartaMs = now.getTime() + (now.getTimezoneOffset() * 60000) + (7 * 60 * 60 * 1000);
    const j = new Date(jakartaMs);
    j.setDate(j.getDate() + 1);
    return j.toISOString().slice(0, 10);
  };

  // Fetch seller's saved address (from db.addresses — same one used as buyer)
  const refreshPickupAddr = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/address/me`);
      const complete = data && data.subdistrict_id && data.address_line && data.phone;
      setPickupAddr(complete ? data : null);
      if (data && data.subdistrict_id) {
        setAddrForm({
          full_name: data.full_name || '',
          phone: data.phone || '',
          address_line: data.address_line || '',
          city: data.city || '',
          province: data.province || '',
          postal_code: data.postal_code || '',
          country: data.country || 'Indonesia',
          latitude: data.latitude || null,
          longitude: data.longitude || null,
          subdistrict_id: data.subdistrict_id || null,
          subdistrict_label: data.subdistrict_label || '',
        });
      }
    } catch {
      setPickupAddr(null);
    }
  }, []);

  const openShipDialog = async (order) => {
    setShipDialog(order);
    setBookForm({ pickup_date: tomorrowISO(), pickup_time: '09:00', pickup_vehicle: 'Motor', size_ack: false });
    setManualShipForm({ courier_name: '', tracking_number: '', shipment_proof_url: '', awb_format_overridden: false });
    if (AUTO_SHIPMENT_ENABLED) {
      await refreshPickupAddr();
    }
  };

  const handleAddressSave = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    try {
      await axios.put(`${API}/address/me`, addrForm);
      toast.success('Address saved');
      setAddrDialogOpen(false);
      await refreshPickupAddr();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save address');
    }
  };

  const handleShipSubmit = async () => {
    if (!pickupAddr) {
      toast.error('Please add your address before booking a shipment');
      setAddrDialogOpen(true);
      return;
    }
    // Validate booking inputs
    if (!bookForm.pickup_date || !bookForm.pickup_time || !bookForm.pickup_vehicle) {
      toast.error('Please fill the pickup date, time, and vehicle');
      return;
    }
    if (bookForm.pickup_date < jakartaTodayISO()) {
      toast.error('Pickup date can\'t be in the past (Jakarta time). Pick today or later.');
      return;
    }
    // Same-day pickups need ≥90 min lead time per Komerce spec. We add a 10-min
    // buffer to cover the API round-trip + the user's hesitation between picking
    // a time and hitting Confirm. The picker UI already enforces this via the
    // `min` attribute; this is a belt-and-braces safety net for typed input.
    if (bookForm.pickup_date === jakartaTodayISO()) {
      const [h, m] = (bookForm.pickup_time || '').split(':').map(Number);
      const nowJkt = new Date(new Date().getTime() + (new Date().getTimezoneOffset() * 60000) + (7 * 3600 * 1000));
      const pickAt = new Date(nowJkt);
      pickAt.setHours(h || 0, m || 0, 0, 0);
      const minLeadMs = 100 * 60 * 1000; // 90 min + 10 min buffer
      if (pickAt.getTime() - nowJkt.getTime() < minLeadMs) {
        toast.error('Pick a later time — same-day pickups need at least 90 minutes notice. Earliest pickable time is shown below the time picker.');
        return;
      }
    }
    if (!bookForm.size_ack) {
      toast.error('Please confirm the parcel size & weight acknowledgement before booking.');
      return;
    }
    setBookSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/orders/${shipDialog.order_id}/book-shipment`, {
        pickup_date: bookForm.pickup_date,
        pickup_time: bookForm.pickup_time,
        pickup_vehicle: bookForm.pickup_vehicle,
      });
      setSales(prev => prev.map(o => o.order_id === shipDialog.order_id ? {
        ...o,
        payment_status: 'shipped',
        courier_name: data.courier_name,
        tracking_number: data.tracking_number,
        ro_order_no: data.ro_order_no,
        ro_pickup_id: data.ro_pickup_id,
        ro_pickup_status: data.ro_pickup_status,
        ro_pickup_date: bookForm.pickup_date,
        ro_pickup_time: bookForm.pickup_time,
        ro_pickup_vehicle: bookForm.pickup_vehicle,
        shipping_method: 'rajaongkir',
      } : o));
      setShipDialog(null);
      fetchOrdersCount();
      const niceAwb = data.tracking_number ? ` · Resi ${data.tracking_number}` : '';
      toast.success(`Shipment booked!${niceAwb}`);
      if (data.ro_pickup_status === 'request_failed') {
        const reason = data.ro_pickup_error ? `: ${data.ro_pickup_error}` : '';
        toast.warning(`Booking succeeded but pickup request failed${reason}. Tap "Retry pickup" on the order card to try again.`, { duration: 8000 });
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.code === 'pickup_address_required') {
        toast.error(detail.message || 'Please complete your address first');
        setPickupAddr(null);
        setAddrDialogOpen(true);
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Failed to book shipment');
      }
    } finally {
      setBookSubmitting(false);
    }
  };

  const handleProofUpload = async (file) => {
    if (!file) return;
    if (!file.type?.startsWith('image/')) {
      toast.error('Please pick an image file (JPG / PNG / HEIC).');
      return;
    }
    setProofUploading(true);
    try {
      // Client-side compression: downscale + JPEG re-encode to stay under 2MB.
      const compressed = await compressImage(file, { maxBytes: 2 * 1024 * 1024, maxDimension: 1600 });
      if (compressed.size > 2 * 1024 * 1024) {
        toast.error('Photo is too large even after compression. Try a different shot.');
        return;
      }
      const fd = new FormData();
      fd.append('file', compressed);
      const { data } = await axios.post(`${API}/upload-evidence`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setManualShipForm(f => ({ ...f, shipment_proof_url: data.url }));
      toast.success('Shipment proof uploaded.');
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Failed to upload proof');
    } finally {
      setProofUploading(false);
    }
  };

  const handleManualShipSubmit = async () => {
    const courier = manualShipForm.courier_name.trim();
    const tracking = manualShipForm.tracking_number.trim();
    const proof = manualShipForm.shipment_proof_url;
    if (!courier) {
      toast.error('Please enter the courier you used (e.g. JNE, J&T, SiCepat).');
      return;
    }
    // Defense in depth — same check runs on every keystroke and disables the
    // button; this safety net protects against programmatic submission.
    const v = validateResi(courier, tracking);
    // Allow override on pattern mismatch only (never on nonsense).
    if (v.hardError && !(v.errorKind === 'pattern' && manualShipForm.awb_format_overridden)) {
      toast.error(v.hardError);
      return;
    }
    if (!proof) {
      toast.error('Please upload a photo of your package showing the resi, or the shipment receipt.');
      return;
    }
    setManualShipSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/orders/${shipDialog.order_id}/ship`, {
        courier_name: courier,
        tracking_number: tracking,
        shipment_proof_url: proof,
        awb_format_overridden: !!manualShipForm.awb_format_overridden,
      });
      // data.soft_hint is intentionally ignored — pattern mismatches now hard-
      // reject upfront on the backend so soft_hint will always be null here.
      void data;
      setSales(prev => prev.map(o => o.order_id === shipDialog.order_id ? {
        ...o,
        payment_status: 'shipped',
        courier_name: courier,
        tracking_number: tracking,
        shipping_method: 'manual',
        shipment_proof_url: proof,
      } : o));
      setShipDialog(null);
      setManualShipForm({ courier_name: '', tracking_number: '', shipment_proof_url: '', awb_format_overridden: false });
      fetchOrdersCount();
      toast.success(`Order marked as shipped · Resi ${tracking}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to mark as shipped');
    } finally {
      setManualShipSubmitting(false);
    }
  };

  const handleDownloadLabel = async (orderId) => {
    try {
      const { data } = await axios.get(`${API}/orders/${orderId}/shipping-label`);
      if (data?.label_url) {
        window.open(data.label_url, '_blank');
      } else {
        toast.error('Label URL not available');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to fetch label');
    }
  };

  const handleRetryPickup = async (orderId) => {
    try {
      const { data } = await axios.post(`${API}/orders/${orderId}/retry-pickup`);
      setSales(prev => prev.map(o => o.order_id === orderId ? {
        ...o,
        ro_pickup_status: 'scheduled',
        ro_pickup_id: data.ro_pickup_id,
        ro_pickup_date: data.ro_pickup_date,
        ro_pickup_time: data.ro_pickup_time,
        ro_pickup_error: null,
      } : o));
      toast.success(`Pickup rescheduled for ${data.ro_pickup_date} ${data.ro_pickup_time}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to retry pickup');
    }
  };

  const handleCancelShipment = async (orderId) => {
    if (!window.confirm('Cancel this shipment? You can rebook afterwards if pickup hasn\'t happened yet.')) return;
    try {
      await axios.post(`${API}/orders/${orderId}/cancel-shipment`, { reason: 'Cancelled by seller' });
      setSales(prev => prev.map(o => o.order_id === orderId ? {
        ...o, payment_status: 'paid', tracking_number: null, courier_name: null,
        ro_pickup_status: 'cancelled', ro_order_no: o.ro_order_no, // keep history
      } : o));
      toast.success('Shipment cancelled');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel shipment');
    }
  };

  const handleSubmitCancel = async () => {
    if (!cancelReason) { toast.error('Please select a reason for cancellation'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/orders/${cancelDialog.order.order_id}/request-cancel`, {
        reason: cancelReason,
      });
      toast.success('Cancellation request submitted. Admin will review it shortly.');
      setSales(prev => prev.map(o =>
        o.order_id === cancelDialog.order.order_id ? { ...o, cancel_requested: true } : o
      ));
      setCancelDialog({ open: false, order: null });
      setCancelReason('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit cancellation request');
    } finally {
      setSubmitting(false);
    }
  };

  // Change-payment-method handlers removed May 2026 — checkout now consolidated
  // to Duitku, which surfaces all payment methods (e-wallet/QRIS/VA) on its
  // hosted page. No need for an on-our-side method switcher anymore.

  const handlePayNow = async (order) => {
    setSubmitting(true);
    try {
      // QRISly orders always use the inline QR checkout page, which re-validates state
      // (and regenerates the QR if expired).
      if (order.payment_provider === 'qrisly') {
        navigate(`/payment/qris?order=${order.order_id}`, { replace: true });
        return;
      }
      // Legacy Duitku: if order already has a payment_url and hasn't expired, route via the
      // interstitial so Back-button navigation re-validates state.
      if (order.payment_url && order.expires_at && new Date(order.expires_at) > new Date()) {
        toast.info('Redirecting to payment page...');
        sessionStorage.setItem('payment_redirect', order.order_id);
        navigate(`/payment/redirect?order=${order.order_id}`, { replace: true });
        return;
      }
      // Otherwise generate a new payment session
      const res = await axios.post(`${API}/orders/${order.order_id}/retry-payment`);
      if (res.data.payment_provider === 'qrisly') {
        navigate(`/payment/qris?order=${order.order_id}`, { replace: true });
      } else if (res.data.payment_url) {
        toast.info('Redirecting to payment page...');
        sessionStorage.setItem('payment_redirect', order.order_id);
        navigate(`/payment/redirect?order=${order.order_id}`, { replace: true });
      } else {
        toast.error('Unable to generate payment link. Please try again.');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to initiate payment');
      // If order expired, update UI
      if (err.response?.status === 400) {
        setPurchases(prev => prev.map(o =>
          o.order_id === order.order_id ? { ...o, payment_status: 'failed' } : o
        ));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleMockPay = async (order) => {
    try {
      await axios.post(`${API}/orders/${order.order_id}/mock-pay`);
      toast.success('Payment simulated! Order is now Paid.');
      setPurchases(prev => prev.map(o => o.order_id === order.order_id ? { ...o, payment_status: 'paid' } : o));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Mock payment failed');
    }
  };

  const handleMessageOrder = async (order, isSeller) => {
    const recipientId = isSeller ? (order.buyer_id || order.user_id) : order.seller_id;
    if (!recipientId) { toast.error('Cannot determine the other party'); return; }
    try {
      const res = await axios.post(`${API}/messages/conversations`, {
        type: 'order',
        order_id: order.order_id,
        recipient_id: recipientId,
      });
      navigate(`/messages/${res.data.conversation_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start conversation');
    }
  };

  const handleUploadEvidence = async (file, type) => {
    setReturnUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/upload-evidence`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const url = `${process.env.REACT_APP_BACKEND_URL}${res.data.url}`;
      if (type === 'video') {
        setReturnForm(f => ({ ...f, video_url: url }));
        toast.success('Unboxing video uploaded');
      } else {
        setReturnForm(f => ({ ...f, photo_urls: [...f.photo_urls, url] }));
        toast.success('Photo uploaded');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to upload ${type}`);
    } finally {
      setReturnUploading(false);
    }
  };

  const handleSubmitReturn = async () => {
    if (!returnForm.listing_id) { toast.error('Please select which item has a problem'); return; }
    if (!returnForm.reason) { toast.error('Please select a reason'); return; }
    // Unboxing video is mandatory for every reason EXCEPT `package_never_arrived`
    // — buyer has no parcel to film when the package never showed up.
    if (returnForm.reason !== 'package_never_arrived' && !returnForm.video_url) {
      toast.error('Unboxing video is required for return requests'); return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/orders/${returnDialog.order.order_id}/request-return`, {
        listing_id: returnForm.listing_id,
        reason: returnForm.reason,
        reason_detail: returnForm.reason_detail,
        video_url: returnForm.video_url,
        photo_urls: returnForm.photo_urls,
      });
      toast.success('Return request submitted. Our team will review your evidence.');
      setPurchases(prev => prev.map(o =>
        o.order_id === returnDialog.order.order_id ? { ...o, has_return_request: true } : o
      ));
      setReturnDialog({ open: false, order: null });
      setReturnForm({ listing_id: '', reason: '', reason_detail: '', video_url: '', photo_urls: [] });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit return request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReturnShipProofUpload = async (file) => {
    if (!file) return;
    if (!file.type?.startsWith('image/')) {
      toast.error('Please pick an image file (JPG / PNG / HEIC).');
      return;
    }
    setReturnShipProofUploading(true);
    try {
      const compressed = await compressImage(file, { maxBytes: 2 * 1024 * 1024, maxDimension: 1600 });
      if (compressed.size > 2 * 1024 * 1024) {
        toast.error('Photo is too large even after compression. Try a different shot.');
        return;
      }
      const fd = new FormData();
      fd.append('file', compressed);
      const { data } = await axios.post(`${API}/upload-evidence`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setReturnShipForm(f => ({ ...f, return_shipment_proof_url: data.url }));
      toast.success('Return shipment proof uploaded.');
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Failed to upload proof');
    } finally {
      setReturnShipProofUploading(false);
    }
  };

  const handleSubmitReturnShip = async () => {
    const courier = returnShipForm.return_courier.trim();
    const tracking = returnShipForm.return_tracking_number.trim();
    const proof = returnShipForm.return_shipment_proof_url;
    if (!courier) { toast.error('Please enter the courier you used (e.g. JNE, J&T, SiCepat).'); return; }
    if (!tracking) { toast.error('Please enter the return tracking number (resi).'); return; }
    // Defense in depth — same check disables the button on every keystroke.
    const v = validateResi(courier, tracking);
    if (v.hardError && !(v.errorKind === 'pattern' && returnShipForm.return_awb_format_overridden)) {
      toast.error(v.hardError); return;
    }
    if (!proof) {
      toast.error('Please upload a photo of the return parcel showing the resi, or the shipment receipt.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/orders/${returnShipDialog.order.order_id}/ship-return`, {
        return_id: returnShipDialog.returnId,
        return_courier: courier,
        return_tracking_number: tracking,
        return_shipment_proof_url: proof,
        return_awb_format_overridden: !!returnShipForm.return_awb_format_overridden,
      });
      toast.success('Return shipment recorded. Seller will be notified.');
      setReturnShipDialog({ open: false, order: null, returnId: '' });
      setReturnShipForm({ return_courier: '', return_tracking_number: '', return_shipment_proof_url: '', return_awb_format_overridden: false });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to record return shipment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmReturnReceived = async (order, returnId) => {
    try {
      await axios.post(`${API}/orders/${order.order_id}/confirm-return-received`, { return_id: returnId });
      toast.success('Receipt confirmed. You have 48 hours to raise a counter-dispute if there\'s an issue with the returned item.');
      // Refetch sales so the order card re-renders with the updated active_return
      // status (return_shipped → return_received) and the Dispute Return button
      // appears immediately without a page reload.
      try {
        const res = await axios.get(`${API}/orders/my-sales`);
        setSales(res.data);
      } catch (_) { /* non-fatal: card status will refresh on next poll */ }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to confirm return receipt');
    }
  };

  const handleCancelReturn = async (order) => {
    // Find the active return for this order
    try {
      const res = await axios.get(`${API}/orders/${order.order_id}/returns`);
      const activeReturn = res.data.find(r => ['pending', 'approved'].includes(r.status));
      if (!activeReturn) { toast.error('No active return request found'); return; }
      if (!window.confirm('Are you sure you want to cancel your return request? You will NOT be able to request a return for this item again.')) return;
      await axios.post(`${API}/orders/${order.order_id}/cancel-return`, { return_id: activeReturn.return_id });
      toast.success('Return request cancelled.');
      setPurchases(prev => prev.map(o =>
        o.order_id === order.order_id ? { ...o, has_return_request: false, [`return_cancelled_${activeReturn.listing_id}`]: true } : o
      ));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel return request');
    }
  };

  const handleSellerDispute = async (order) => {
    try {
      const res = await axios.get(`${API}/orders/${order.order_id}/returns`);
      const activeReturn = res.data.find(r => ['return_shipped', 'return_received'].includes(r.status) && !r.seller_dispute);
      if (!activeReturn) { toast.error('No eligible return to dispute'); return; }
      setSellerDisputeForm({ reason: '', reason_detail: '', photo_urls: [] });
      setSellerDisputeDialog({ open: true, order, returnId: activeReturn.return_id });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load return info');
    }
  };

  const handleSubmitSellerDispute = async () => {
    if (!sellerDisputeForm.reason) { toast.error('Please select a reason'); return; }
    if (sellerDisputeForm.photo_urls.length < 1) { toast.error('At least 1 photo is required'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/orders/${sellerDisputeDialog.order.order_id}/seller-dispute`, {
        return_id: sellerDisputeDialog.returnId,
        reason: sellerDisputeForm.reason,
        reason_detail: sellerDisputeForm.reason_detail,
        photo_urls: sellerDisputeForm.photo_urls,
      });
      toast.success('Dispute submitted. Admin will review both sides.');
      setSellerDisputeDialog({ open: false, order: null, returnId: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit dispute');
    } finally {
      setSubmitting(false);
    }
  };

  const currentOrders = tab === 'purchases' ? purchases : sales;
  const newSalesCount = sales.filter(o => (o.payment_status || o.status) === 'paid').length;

  return (
    <div className="min-h-screen bg-[#FCFAF8]">
      <Header />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        <button onClick={() => navigate('/')} className="flex items-center gap-1 text-sm font-body text-[#78716C] hover:text-[#2C2A29] mb-4" data-testid="orders-back">
          <ArrowLeft size={16} /> Back
        </button>

        <h1 className="font-heading text-3xl text-[#2C2A29] mb-6" data-testid="orders-title">My Orders</h1>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 bg-cream-50 rounded-full p-1 w-fit" data-testid="orders-tabs">
          <button onClick={() => setTab('purchases')}
            className={`rounded-full px-5 py-2 text-sm font-body font-medium transition-all ${
              tab === 'purchases' ? 'bg-white text-[#2C2A29] shadow-sm' : 'text-[#78716C] hover:text-[#2C2A29]'}`}
            data-testid="tab-purchases">
            My Purchases
          </button>
          <button onClick={() => setTab('sales')}
            className={`relative rounded-full px-5 py-2 text-sm font-body font-medium transition-all ${
              tab === 'sales' ? 'bg-white text-[#2C2A29] shadow-sm' : 'text-[#78716C] hover:text-[#2C2A29]'}`}
            data-testid="tab-sales">
            My Sales
            {newSalesCount > 0 && (
              <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] flex items-center justify-center bg-red-500 text-white text-[10px] font-bold rounded-full px-1" data-testid="new-sales-badge">
                {newSalesCount}
              </span>
            )}
          </button>
        </div>

        {loading ? (
          <div className="space-y-4">
            {[1,2,3].map(i => <div key={i} className="h-32 bg-cream-100 rounded-2xl animate-pulse" />)}
          </div>
        ) : currentOrders.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-3xl border border-cream-100/50" data-testid="no-orders">
            <ShoppingBag size={40} className="mx-auto text-cream-200 mb-3" />
            <p className="font-body text-[#78716C] mb-2">
              {tab === 'purchases' ? 'No purchases yet' : 'No sales yet'}
            </p>
            <p className="font-body text-sm text-[#A89F91] mb-4">
              {tab === 'purchases' ? 'Start shopping to see your orders here' : 'Your sold items will appear here'}
            </p>
            {tab === 'purchases' && (
              <button onClick={() => navigate('/')}
                className="bg-[#F4C2C2] text-[#2C2A29] rounded-full px-6 py-2.5 font-body font-semibold hover:bg-[#ECAEAE] transition-all">
                Browse Products
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {/* New order banner for sellers */}
            {tab === 'sales' && newSalesCount > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded-2xl px-4 py-3 flex items-center gap-3" data-testid="new-order-banner">
                <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <Package size={16} className="text-blue-600" />
                </div>
                <p className="font-body text-sm text-blue-800">
                  You have <span className="font-semibold">{newSalesCount} new paid order{newSalesCount > 1 ? 's' : ''}</span> waiting to be shipped.
                </p>
              </div>
            )}
            {currentOrders.map(order => (
              <OrderCard
                key={order.order_id}
                order={order}
                isSeller={tab === 'sales'}
                isAdmin={!!currentUser?.is_admin}
                onShip={o => openShipDialog(o)}
                onConfirm={handleConfirmReceived}
                onRequestCancel={o => { setCancelDialog({ open: true, order: o }); setCancelReason(''); }}
                onPayNow={handlePayNow}
                onMockPay={handleMockPay}
                onMessage={handleMessageOrder}
                onRequestReturn={o => {
                  const items = o.items || [{ listing_id: o.listing_id, product_name: o.product_name, brand: o.brand, photo: o.photo }];
                  setReturnForm({ listing_id: items.length === 1 ? items[0].listing_id : '', reason: '', reason_detail: '', video_url: '', photo_urls: [] });
                  setReturnDialog({ open: true, order: o });
                }}
                onShipReturn={(o, retId) => { setReturnShipDialog({ open: true, order: o, returnId: retId }); setReturnShipForm({ return_courier: '', return_tracking_number: '', return_shipment_proof_url: '', return_awb_format_overridden: false }); }}
                onConfirmReturnReceived={handleConfirmReturnReceived}
                onCancelReturn={handleCancelReturn}
                onSellerDispute={handleSellerDispute}
                onDownloadLabel={handleDownloadLabel}
                onCancelShipment={handleCancelShipment}
              />
            ))}
          </div>
        )}
      </main>

      {/* Ship Dialog — manual flow (current v1 launch) or automated Komerce flow (gated behind AUTO_SHIPMENT_ENABLED). */}
      <Dialog open={!!shipDialog} onOpenChange={() => setShipDialog(null)}>
        <DialogContent className="rounded-3xl border-cream-100 bg-white max-w-lg mx-4 max-h-[90vh] overflow-y-auto" data-testid="ship-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-[#2C2A29] flex items-center gap-2">
              <Truck size={20} className="text-blue-600" /> {AUTO_SHIPMENT_ENABLED ? 'Ready to ship?' : 'Mark as Shipped'}
            </DialogTitle>
            <DialogDescription className="font-body text-[#78716C]">
              {AUTO_SHIPMENT_ENABLED
                ? <>Order #{shipDialog?.order_id} &middot; A courier will pick the parcel up from your address. Your AWB / resi &amp; shipping label will be generated automatically.</>
                : <>Order #{shipDialog?.order_id} &middot; Ship the parcel with the courier of your choice, then enter the tracking number below so the buyer can follow the delivery.</>
              }
            </DialogDescription>
          </DialogHeader>

          {!AUTO_SHIPMENT_ENABLED && (
            <div className="space-y-4 mt-2" data-testid="manual-ship-form">
              {/* Ship-to recap — same data as the order card, surfaced here at
                  the exact moment the seller is about to write the address on
                  the parcel + enter the resi. Easy copy button included. */}
              {shipDialog?.buyer_address_snapshot && (() => {
                const ba = shipDialog.buyer_address_snapshot;
                const copyText = [
                  ba.full_name && `${ba.full_name} (${ba.phone || 'no phone'})`,
                  ba.address_line,
                  ba.subdistrict_label,
                  [ba.city, ba.province, ba.postal_code].filter(Boolean).join(', '),
                  ba.country,
                ].filter(Boolean).join('\n');
                const onCopy = async () => {
                  try { await navigator.clipboard.writeText(copyText); toast.success('Address copied'); }
                  catch { toast.error('Could not copy — please copy manually'); }
                };
                return (
                  <div className="bg-amber-50 border border-amber-200 rounded-2xl px-3 py-2.5 text-sm font-body" data-testid="ship-dialog-ship-to">
                    <div className="flex items-center justify-between gap-2 mb-1.5">
                      <span className="text-xs font-semibold uppercase tracking-wider text-amber-900 flex items-center gap-1.5">
                        <MapPin size={12} /> Ship to
                      </span>
                      <button type="button" onClick={onCopy}
                        className="text-xs font-body text-amber-700 hover:text-amber-900 inline-flex items-center gap-1 underline-offset-2 hover:underline"
                        data-testid="ship-dialog-copy-ship-to">
                        <Copy size={11} /> Copy
                      </button>
                    </div>
                    <p className="text-sm font-body font-medium text-amber-900 leading-snug">{ba.full_name || '—'}{ba.phone && <span className="text-amber-800 font-normal"> · {ba.phone}</span>}</p>
                    <p className="text-sm font-body text-amber-900 leading-snug">{ba.address_line || '—'}</p>
                    <p className="text-xs font-body text-amber-800 leading-snug">
                      {[ba.subdistrict_label, ba.city, ba.province, ba.postal_code].filter(Boolean).join(', ')}
                      {ba.country && [ba.subdistrict_label, ba.city, ba.province, ba.postal_code].filter(Boolean).length > 0 ? ` · ${ba.country}` : ba.country}
                    </p>
                    {ba.is_one_off && (
                      <p className="text-xs font-body text-amber-700 italic mt-1">Buyer chose a one-time delivery address for this order.</p>
                    )}
                  </div>
                );
              })()}
              <div>
                <label className="block text-xs font-body font-medium text-[#78716C] mb-1">Courier *</label>
                <input
                  type="text"
                  list="manual-courier-suggestions"
                  value={manualShipForm.courier_name}
                  onChange={e => setManualShipForm(f => ({ ...f, courier_name: e.target.value }))}
                  placeholder="e.g. JNE, J&T, SiCepat, AnterAja, Pos Indonesia"
                  className="w-full bg-cream-50 border border-cream-100 rounded-xl px-3 py-2.5 font-body text-sm text-[#2C2A29]"
                  data-testid="manual-courier-input"
                />
                <datalist id="manual-courier-suggestions">
                  <option value="JNE" />
                  <option value="J&T" />
                  <option value="SiCepat" />
                  <option value="AnterAja" />
                  <option value="ID Express" />
                  <option value="Pos Indonesia" />
                  <option value="Ninja Xpress" />
                  <option value="Lion Parcel" />
                </datalist>
              </div>
              <div>
                <label className="block text-xs font-body font-medium text-[#78716C] mb-1">Tracking number (resi / AWB) *</label>
                <input
                  type="text"
                  value={manualShipForm.tracking_number}
                  onChange={e => {
                    // Auto-normalize as the seller types: strip ALL whitespace
                    // (not just trim) and uppercase. Catches legitimate paste
                    // cases like "jt 2845 8329 01" → "JT2845832901".
                    const cleaned = e.target.value.replace(/\s+/g, '').toUpperCase();
                    setManualShipForm(f => ({ ...f, tracking_number: cleaned }));
                  }}
                  placeholder="Type or paste the resi number from your courier receipt"
                  className={`w-full bg-cream-50 border rounded-xl px-3 py-2.5 font-body text-sm text-[#2C2A29] focus:outline-none focus:ring-2 ${
                    manualShipTrackingTouched && manualShipResi.hardError
                      ? 'border-red-300 focus:ring-red-200'
                      : manualShipTrackingTouched
                      ? 'border-emerald-300 focus:ring-emerald-200'
                      : 'border-cream-100 focus:ring-cream-200'
                  }`}
                  data-testid="manual-tracking-input"
                />
                {manualShipTrackingTouched && manualShipResi.hardError && (
                  <p className="text-xs font-body text-red-700 mt-1.5 leading-snug flex items-start gap-1.5" data-testid="manual-tracking-error">
                    <AlertTriangle size={12} className="mt-0.5 flex-shrink-0" />
                    <span>{manualShipResi.hardError}</span>
                  </p>
                )}
                {/* Escape hatch — only for PATTERN mismatches (not nonsense).
                    Lets sellers override the strict per-courier regex when
                    they genuinely have a valid AWB our pattern doesn't cover
                    yet. Override is logged to awb_rejection_log for admin
                    pattern-tuning. */}
                {manualShipTrackingTouched && manualShipResi.errorKind === 'pattern' && (
                  <label className="mt-2 flex items-start gap-2 text-xs font-body text-[#525252] bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 cursor-pointer hover:bg-amber-100 transition-all" data-testid="awb-override-label">
                    <input
                      type="checkbox"
                      checked={manualShipForm.awb_format_overridden}
                      onChange={e => setManualShipForm(f => ({ ...f, awb_format_overridden: e.target.checked }))}
                      className="mt-0.5 flex-shrink-0 accent-amber-700"
                      data-testid="awb-override-checkbox"
                    />
                    <span className="leading-snug">I confirm this AWB is correct as printed on my courier receipt. <span className="text-amber-800">Admin will spot-check before payout.</span></span>
                  </label>
                )}
              </div>
              <div>
                <label className="block text-xs font-body font-medium text-[#78716C] mb-1">Shipment proof photo *</label>
                <p className="text-xs font-body text-[#A89F91] mb-2 leading-relaxed">
                  Upload a photo of your package showing the resi attached to it, or a photo of the shipment receipt (nota pengiriman).
                </p>
                {manualShipForm.shipment_proof_url ? (
                  <div className="relative w-full rounded-xl overflow-hidden border border-emerald-200 bg-emerald-50" data-testid="ship-proof-preview">
                    <img
                      src={manualShipForm.shipment_proof_url.startsWith('http') ? manualShipForm.shipment_proof_url : `${process.env.REACT_APP_BACKEND_URL}${manualShipForm.shipment_proof_url}`}
                      alt="Shipment proof"
                      className="w-full max-h-64 object-contain bg-cream-50"
                    />
                    <button
                      type="button"
                      onClick={() => setManualShipForm(f => ({ ...f, shipment_proof_url: '' }))}
                      className="absolute top-2 right-2 bg-white/95 hover:bg-white text-red-600 rounded-full p-1.5 shadow-sm transition-all"
                      data-testid="ship-proof-remove-btn"
                      title="Remove and re-upload"
                    >
                      <XCircle size={16} />
                    </button>
                  </div>
                ) : (
                  <label
                    htmlFor="ship-proof-file"
                    className={`w-full flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed py-6 px-3 transition-all cursor-pointer ${
                      proofUploading
                        ? 'border-blue-200 bg-blue-50 text-blue-700'
                        : 'border-cream-200 bg-cream-50 hover:border-blue-300 hover:bg-blue-50 text-[#78716C] hover:text-blue-700'
                    }`}
                    data-testid="ship-proof-upload-label"
                  >
                    {proofUploading ? (
                      <>
                        <Loader2 size={20} className="animate-spin" />
                        <span className="text-sm font-body">Compressing & uploading...</span>
                      </>
                    ) : (
                      <>
                        <Camera size={20} />
                        <span className="text-sm font-body font-medium">Tap to upload photo</span>
                        <span className="text-xs font-body text-[#A89F91]">JPG / PNG · auto-compressed to ≤ 2 MB</span>
                      </>
                    )}
                    <input
                      id="ship-proof-file"
                      type="file"
                      accept="image/*"
                      capture="environment"
                      className="hidden"
                      disabled={proofUploading}
                      onChange={e => handleProofUpload(e.target.files?.[0])}
                      data-testid="ship-proof-file-input"
                    />
                  </label>
                )}
              </div>
              <div className="bg-blue-50 rounded-xl p-3 text-xs font-body text-blue-800 border border-blue-100">
                Once you tap <span className="font-semibold">Mark as Shipped</span>, the buyer will be notified by email and the order status will switch to <span className="font-semibold">Shipped</span>. Make sure the tracking number is correct — buyers will use it to follow their parcel.
              </div>
              <button onClick={handleManualShipSubmit} disabled={manualShipSubmitting || proofUploading || !manualShipForm.shipment_proof_url || !manualShipForm.courier_name.trim() || manualShipBlocked}
                className="w-full bg-blue-600 text-white rounded-full px-6 py-3 font-body font-semibold hover:bg-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                data-testid="submit-manual-ship-btn">
                {manualShipSubmitting ? <><Loader2 size={16} className="animate-spin" /> Marking as shipped...</> : 'Mark as Shipped'}
              </button>
            </div>
          )}

          {AUTO_SHIPMENT_ENABLED && (
          <div className="space-y-4 mt-2">
            {/* ── Pickup address section ── */}
            {!pickupAddr ? (
              <div className="space-y-3 bg-amber-50 rounded-2xl p-4 border border-amber-200" data-testid="pickup-address-form">
                <div className="text-sm font-body font-semibold text-amber-900 flex items-center gap-1.5">
                  <Package size={14} /> Complete your address to book shipments
                </div>
                <p className="text-xs font-body text-amber-700">
                  We use the same address you set on your profile (for receiving your orders) as your pickup address. Add or update it once — we'll reuse it for every future shipment.
                </p>
                <button onClick={() => setAddrDialogOpen(true)}
                  className="w-full bg-amber-600 text-white rounded-full px-4 py-2 text-sm font-body font-semibold hover:bg-amber-700 transition-all"
                  data-testid="open-address-dialog-btn">
                  Add / Update Address
                </button>
              </div>
            ) : (
              <div className="bg-emerald-50 rounded-2xl p-3 border border-emerald-200">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-body font-semibold text-emerald-900 mb-0.5">Pickup from</div>
                    <div className="text-sm font-body text-emerald-900">{pickupAddr.full_name} · {pickupAddr.phone}</div>
                    <div className="text-xs font-body text-emerald-800 truncate">{pickupAddr.address_line}{pickupAddr.postal_code ? `, ${pickupAddr.postal_code}` : ''}</div>
                    <div className="text-xs font-body text-emerald-700 truncate">
                      {shipDialog?.shipper_subdistrict_label || pickupAddr.subdistrict_label}
                    </div>
                    {shipDialog?.shipper_subdistrict_label &&
                     pickupAddr.subdistrict_label &&
                     shipDialog.shipper_subdistrict_label !== pickupAddr.subdistrict_label && (
                      <div className="mt-1.5 text-xs font-body text-amber-800 bg-amber-50 rounded-md px-2 py-1 border border-amber-200" data-testid="shipper-snapshot-warning">
                        Note: this order ships from your address at sale time, not your current profile kecamatan. The buyer paid against this origin.
                      </div>
                    )}
                  </div>
                  <button onClick={() => setAddrDialogOpen(true)} className="text-xs font-body text-emerald-700 underline whitespace-nowrap" data-testid="edit-pickup-address-btn">
                    Edit
                  </button>
                </div>
              </div>
            )}

            {/* ── Booking inputs (only if pickup address is set) ── */}
            {pickupAddr && (() => {
              // Compute the earliest pickup_time the user may select. Komerce's
              // documented rule: when pickup_date == today (Jakarta), pickup_time
              // must be at least 90 minutes from "now". Round UP to the next
              // full 30-min mark for a clean dropdown experience. For future
              // dates, anything 08:00 onwards is fine.
              const nowJkt = new Date(new Date().getTime() + (new Date().getTimezoneOffset() * 60000) + (7 * 3600 * 1000));
              const cutoffMs = nowJkt.getTime() + 90 * 60 * 1000;
              const cutoff = new Date(cutoffMs);
              // Round to next 30-min mark
              const cmin = cutoff.getMinutes() <= 30 ? 30 : 60;
              const cutoffRounded = new Date(cutoff);
              cutoffRounded.setMinutes(cmin === 60 ? 0 : 30);
              if (cmin === 60) cutoffRounded.setHours(cutoff.getHours() + 1);
              cutoffRounded.setSeconds(0);
              const pad = n => String(n).padStart(2, '0');
              const minTimeToday = `${pad(cutoffRounded.getHours())}:${pad(cutoffRounded.getMinutes())}`;
              const isToday = bookForm.pickup_date === jakartaTodayISO();
              const minTimeAttr = isToday ? minTimeToday : '08:00';
              return (
              <div className="space-y-3" data-testid="book-shipment-form">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs font-body font-medium text-[#78716C] mb-1">Pickup Date *</label>
                    <input type="date" value={bookForm.pickup_date} onChange={e => setBookForm(f => ({ ...f, pickup_date: e.target.value }))}
                      min={jakartaTodayISO()} data-testid="pickup-date-input"
                      className="w-full bg-cream-50 border border-cream-100 rounded-xl px-3 py-2.5 font-body text-sm text-[#2C2A29]" />
                  </div>
                  <div>
                    <label className="block text-xs font-body font-medium text-[#78716C] mb-1">Pickup Time *</label>
                    <input type="time" value={bookForm.pickup_time} onChange={e => setBookForm(f => ({ ...f, pickup_time: e.target.value }))}
                      min={minTimeAttr}
                      data-testid="pickup-time-input"
                      className="w-full bg-cream-50 border border-cream-100 rounded-xl px-3 py-2.5 font-body text-sm text-[#2C2A29]" />
                    {isToday && (
                      <div className="text-[11px] font-body text-[#A89F91] mt-1" data-testid="pickup-cutoff-hint">
                        Same-day pickups require at least 90 min lead time. Earliest today: <strong className="text-[#78716C]">{minTimeToday}</strong>.
                      </div>
                    )}
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-body font-medium text-[#78716C] mb-1">Pickup Vehicle</label>
                  <div className="grid grid-cols-3 gap-2">
                    {['Motor', 'Mobil', 'Truk'].map(v => (
                      <button key={v} type="button" onClick={() => setBookForm(f => ({ ...f, pickup_vehicle: v }))}
                        data-testid={`pickup-vehicle-${v.toLowerCase()}`}
                        className={`rounded-full px-3 py-2 text-sm font-body font-medium transition-all ${
                          bookForm.pickup_vehicle === v ? 'bg-blue-600 text-white' : 'bg-cream-50 text-[#78716C] hover:bg-cream-100'
                        }`}>{v}</button>
                    ))}
                  </div>
                  <div className="text-xs font-body text-[#A89F91] mt-1">Recommended: Motor for parcels under 5 kg.</div>
                </div>
                <div className="bg-blue-50 rounded-xl p-3 text-xs font-body text-blue-800 space-y-0.5 border border-blue-100">
                  <div>📦 Your AWB / resi will be generated automatically (may take a few hours depending on the provider).</div>
                  <div>📥 You'll get a PDF shipping label to print and stick on the parcel.</div>
                  <div>🚚 The pickup courier should arrive around the date &amp; time you set above, but exact timing depends on the provider.</div>
                  <div>⏰ <span className="font-semibold">If no pickup happens within 24 hours,</span> please email us at <a href="mailto:hello@stashmart.store" className="underline">hello@stashmart.store</a> with your Order ID so we can follow up with the provider.</div>
                </div>
                {/* Size & weight acknowledgement — required before booking.
                    Indonesian couriers bill on max(actual_weight, volumetric_weight).
                    Volumetric kg = L×W×H / 6000. Staying under 6000 cm³ keeps the
                    parcel in the 1 kg billing tier. Approx max box ≈ 20×15×20 cm
                    ("about a shoebox"). The seller self-certifies to shift the
                    liability for any oversize re-weigh fees onto them. */}
                <label className="flex items-start gap-2.5 bg-amber-50 border border-amber-200 rounded-xl p-3 cursor-pointer" data-testid="size-ack-label">
                  <input type="checkbox"
                    checked={bookForm.size_ack}
                    onChange={e => setBookForm(f => ({ ...f, size_ack: e.target.checked }))}
                    className="mt-0.5 w-4 h-4 accent-amber-600 cursor-pointer"
                    data-testid="size-ack-checkbox" />
                  <span className="text-xs font-body text-amber-900 leading-relaxed">
                    <span className="font-semibold">I confirm this parcel weighs no more than 1 kg and is no larger than a small shoebox</span> (roughly 20×15×20 cm). I understand that if the parcel exceeds this limit, the shipping provider may charge an additional fee at sortation, and <span className="font-semibold">I will be responsible for covering that extra cost.</span>
                  </span>
                </label>
                <button onClick={handleShipSubmit} disabled={bookSubmitting || !bookForm.size_ack}
                  className="w-full bg-blue-600 text-white rounded-full px-6 py-3 font-body font-semibold hover:bg-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  data-testid="submit-ship-btn">
                  {bookSubmitting ? <><Loader2 size={16} className="animate-spin" /> Booking shipment...</> : 'Confirm Booking'}
                </button>
              </div>
              );
            })()}
          </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Change Payment Method Dialog — removed May 2026 after consolidating
          to Duitku-only checkout. Buyer picks method on Duitku's hosted page. */}


      {/* Cancel Request Dialog (Seller) */}
      <Dialog open={cancelDialog.open} onOpenChange={(v) => { if (!v) { setCancelDialog({ open: false, order: null }); setCancelReason(''); } }}>
        <DialogContent className="rounded-3xl border-cream-100 bg-white max-w-md mx-4" data-testid="cancel-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-[#2C2A29]">Request Order Cancellation</DialogTitle>
            <DialogDescription className="font-body text-[#78716C]">
              Order #{cancelDialog.order?.order_id} &middot; {cancelDialog.order?.amount ? formatPrice(cancelDialog.order.amount) : ''}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <label className="block text-xs font-body font-medium text-[#78716C] mb-1">Reason for cancellation *</label>
              <select
                value={cancelReason}
                onChange={e => setCancelReason(e.target.value)}
                className="w-full bg-cream-50 border border-cream-100 rounded-xl px-3 py-2.5 font-body text-sm text-[#2C2A29] focus:outline-none focus:ring-2 focus:ring-blush-200 focus:border-transparent"
                data-testid="cancel-reason-select"
              >
                <option value="">Select a reason...</option>
                <option value="Out of stock">Out of stock</option>
                <option value="Product damaged / no longer in sellable condition">Product damaged / no longer in sellable condition</option>
                <option value="Listing price was incorrect">Listing price was incorrect</option>
                <option value="Unable to ship within the required timeframe">Unable to ship within the required timeframe</option>
                <option value="Buyer requested cancellation">Buyer requested cancellation</option>
              </select>
            </div>
            <p className="text-xs font-body text-[#A89F91]">
              Admin will review your request. If approved, the buyer will be refunded and stock will be restored.
            </p>
            <div className="flex gap-3">
              <button onClick={() => { setCancelDialog({ open: false, order: null }); setCancelReason(''); }}
                className="flex-1 border border-cream-100 text-[#2C2A29] rounded-full px-4 py-2.5 font-body font-semibold hover:bg-cream-50 transition-all"
                data-testid="cancel-dialog-cancel-btn">Back</button>
              <button onClick={handleSubmitCancel} disabled={submitting}
                className="flex-1 bg-[#F4C2C2] text-[#2C2A29] rounded-full px-4 py-2.5 font-body font-semibold hover:bg-[#ECAEAE] transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="cancel-submit-btn">
                {submitting ? <><Loader2 size={14} className="animate-spin" /> Submitting...</> : 'Submit Request'}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Return Request Dialog */}
      <Dialog open={returnDialog.open} onOpenChange={(v) => { if (!v) { setReturnDialog({ open: false, order: null }); setReturnForm({ listing_id: '', reason: '', reason_detail: '', video_url: '', photo_urls: [] }); } }}>
        <DialogContent className="rounded-3xl border-cream-100 bg-white max-w-lg mx-4 max-h-[90vh] overflow-y-auto" data-testid="return-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-[#2C2A29]">Report a Problem</DialogTitle>
            <DialogDescription className="font-body text-[#78716C]">
              Order #{returnDialog.order?.order_id}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            {/* Select item */}
            {returnDialog.order && (() => {
              const items = returnDialog.order.items || [{ listing_id: returnDialog.order.listing_id, product_name: returnDialog.order.product_name, brand: returnDialog.order.brand, photo: returnDialog.order.photo, item_price: returnDialog.order.item_price, quantity: returnDialog.order.quantity }];
              return items.length > 1 ? (
                <div>
                  <label className="block text-xs font-body font-medium text-[#78716C] mb-2">Which item has a problem? *</label>
                  <div className="space-y-2">
                    {items.map(item => (
                      <button key={item.listing_id} type="button"
                        onClick={() => setReturnForm(f => ({ ...f, listing_id: item.listing_id }))}
                        className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-all text-left ${
                          returnForm.listing_id === item.listing_id ? 'border-orange-300 bg-orange-50' : 'border-cream-100 hover:border-orange-200'
                        }`}
                        data-testid={`return-item-${item.listing_id}`}>
                        {item.photo && <img src={item.photo} alt="" className="w-12 h-12 rounded-lg object-cover flex-shrink-0" />}
                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-body uppercase text-[#A89F91]">{item.brand}</p>
                          <p className="text-sm font-body font-medium text-[#2C2A29] truncate">{item.product_name}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              ) : null;
            })()}

            {/* Reason */}
            <div>
              <label className="block text-xs font-body font-medium text-[#78716C] mb-2">What's the problem? *</label>
              <div className="space-y-2">
                {RETURN_REASONS.map(r => (
                  <button key={r.value} type="button"
                    onClick={() => setReturnForm(f => ({ ...f, reason: r.value }))}
                    className={`w-full text-left px-4 py-3 rounded-xl border text-sm font-body transition-all ${
                      returnForm.reason === r.value ? 'border-orange-300 bg-orange-50 font-medium text-[#2C2A29]' : 'border-cream-100 text-[#78716C] hover:border-orange-200'
                    }`}
                    data-testid={`return-reason-${r.value}`}>
                    {r.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Additional details */}
            <div>
              <label className="block text-xs font-body font-medium text-[#78716C] mb-1">Additional details</label>
              <textarea value={returnForm.reason_detail}
                onChange={e => setReturnForm(f => ({ ...f, reason_detail: e.target.value }))}
                placeholder="Describe the issue..."
                className="w-full bg-cream-50 border border-cream-100 rounded-xl px-3 py-2.5 font-body text-sm text-[#2C2A29] focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-transparent resize-none"
                rows={2} data-testid="return-detail-input" />
            </div>

            {/* Video upload */}
            <div>
              {returnForm.reason === 'package_never_arrived' ? (
                <>
                  <label className="block text-xs font-body font-medium text-[#78716C] mb-1">Unboxing video <span className="text-[#A89F91] font-normal">(not required for this reason)</span></label>
                  <p className="text-xs font-body text-[#A89F91] mb-2">Since the parcel never arrived, there's nothing to unbox. Skip this and use the photos slot below for courier-app screenshots or the tracking page showing the issue.</p>
                </>
              ) : (
                <>
                  <label className="block text-xs font-body font-medium text-[#78716C] mb-1">Unboxing video * <span className="text-[#A89F91] font-normal">(max 100MB)</span></label>
                  <p className="text-xs font-body text-[#A89F91] mb-2">Show unopened parcel, shipping label, full unboxing, and the issue. No video = likely rejected.</p>
                </>
              )}
              {returnForm.video_url ? (
                <div className="flex items-center gap-2 bg-green-50 rounded-xl px-3 py-2">
                  <Video size={16} className="text-green-600" />
                  <span className="text-xs font-body text-green-700 font-medium flex-1">Video uploaded</span>
                  <button type="button" onClick={() => setReturnForm(f => ({ ...f, video_url: '' }))}
                    className="text-xs text-red-400 hover:text-red-600">Remove</button>
                </div>
              ) : (
                <label className={`flex items-center justify-center gap-2 border-2 border-dashed rounded-xl px-4 py-4 cursor-pointer transition-all ${
                  returnUploading ? 'border-orange-200 bg-orange-50' : 'border-cream-200 hover:border-orange-300 hover:bg-orange-50/30'
                }`}>
                  {returnUploading ? (
                    <><Loader2 size={18} className="animate-spin text-orange-500" /><span className="text-sm font-body text-orange-600">Uploading...</span></>
                  ) : (
                    <><Upload size={18} className="text-[#A89F91]" /><span className="text-sm font-body text-[#78716C]">Upload unboxing video</span></>
                  )}
                  <input type="file" accept="video/*" className="hidden" disabled={returnUploading}
                    onChange={e => { if (e.target.files[0]) handleUploadEvidence(e.target.files[0], 'video'); }}
                    data-testid="return-video-input" />
                </label>
              )}
            </div>

            {/* Photo upload */}
            <div>
              <label className="block text-xs font-body font-medium text-[#78716C] mb-1">Photos <span className="text-[#A89F91] font-normal">(optional, up to 3 — helps us decide faster)</span></label>
              <div className="grid grid-cols-3 gap-2" data-testid="return-photo-slots">
                {[0, 1, 2].map(i => {
                  const url = returnForm.photo_urls[i];
                  if (url) {
                    return (
                      <div key={i} className="relative aspect-square">
                        <img src={url} alt="" className="w-full h-full rounded-lg object-cover" />
                        <button type="button"
                          onClick={() => setReturnForm(f => ({ ...f, photo_urls: f.photo_urls.filter((_, j) => j !== i) }))}
                          className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-[10px] leading-none"
                          data-testid={`return-photo-remove-${i}`}>×</button>
                      </div>
                    );
                  }
                  // Next-empty slot is the active uploader; later empty slots are placeholders
                  const isActive = i === returnForm.photo_urls.length;
                  return (
                    <label key={i}
                      className={`aspect-square border-2 border-dashed rounded-lg flex items-center justify-center transition-all ${
                        isActive ? 'border-cream-200 hover:border-orange-300 cursor-pointer' : 'border-cream-100 bg-cream-50/30 cursor-not-allowed opacity-60'
                      }`}
                      data-testid={`return-photo-slot-${i}`}>
                      <Camera size={18} className="text-[#A89F91]" />
                      <input type="file" accept="image/*" className="hidden"
                        disabled={!isActive || returnUploading}
                        onChange={e => { if (e.target.files[0]) handleUploadEvidence(e.target.files[0], 'photo'); }}
                        data-testid={i === 0 ? 'return-photo-input' : `return-photo-input-${i}`} />
                    </label>
                  );
                })}
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-100 rounded-xl px-3 py-2 text-xs font-body text-amber-700 leading-relaxed">
              Returns are only accepted for seller's fault. Change of mind, scent/texture preference, or shade mismatch are not eligible. Buyer pays return shipping if required.
            </div>

            <div className="flex gap-3">
              <button onClick={() => { setReturnDialog({ open: false, order: null }); setReturnForm({ listing_id: '', reason: '', reason_detail: '', video_url: '', photo_urls: [] }); }}
                className="flex-1 border border-cream-100 text-[#2C2A29] rounded-full px-4 py-2.5 font-body font-semibold hover:bg-cream-50 transition-all"
                data-testid="return-cancel-btn">Cancel</button>
              <button onClick={handleSubmitReturn} disabled={submitting || returnUploading}
                className="flex-1 bg-orange-100 text-orange-800 rounded-full px-4 py-2.5 font-body font-semibold hover:bg-orange-200 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="return-submit-btn">
                {submitting ? <><Loader2 size={14} className="animate-spin" /> Submitting...</> : 'Submit Return Request'}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Return Ship Dialog (Buyer ships back) */}
      <Dialog open={returnShipDialog.open} onOpenChange={(v) => { if (!v) { setReturnShipDialog({ open: false, order: null, returnId: '' }); setReturnShipForm({ return_courier: '', return_tracking_number: '', return_shipment_proof_url: '', return_awb_format_overridden: false }); } }}>
        <DialogContent className="rounded-3xl border-cream-100 bg-white max-w-md mx-4 max-h-[90vh] overflow-y-auto" data-testid="return-ship-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-[#2C2A29]">Ship Return Item</DialogTitle>
            <DialogDescription className="font-body text-[#78716C]">
              Enter return shipping details. You pay the return shipping cost.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2" data-testid="return-ship-form">
            <div>
              <label className="block text-xs font-body font-medium text-[#78716C] mb-1">Courier *</label>
              <input
                type="text"
                list="return-courier-suggestions"
                value={returnShipForm.return_courier}
                onChange={e => setReturnShipForm(f => ({ ...f, return_courier: e.target.value }))}
                placeholder="e.g. JNE, J&T, SiCepat, AnterAja, Pos Indonesia"
                className="w-full bg-cream-50 border border-cream-100 rounded-xl px-3 py-2.5 font-body text-sm text-[#2C2A29]"
                data-testid="return-courier-input"
              />
              <datalist id="return-courier-suggestions">
                <option value="JNE" />
                <option value="J&T" />
                <option value="SiCepat" />
                <option value="AnterAja" />
                <option value="ID Express" />
                <option value="Pos Indonesia" />
                <option value="Ninja Xpress" />
                <option value="Lion Parcel" />
              </datalist>
            </div>
            <div>
              <label className="block text-xs font-body font-medium text-[#78716C] mb-1">Tracking number (resi / AWB) *</label>
              <input
                type="text"
                value={returnShipForm.return_tracking_number}
                onChange={e => {
                  // Auto-normalize as the buyer types: strip whitespace, uppercase.
                  const cleaned = e.target.value.replace(/\s+/g, '').toUpperCase();
                  setReturnShipForm(f => ({ ...f, return_tracking_number: cleaned }));
                }}
                placeholder="Type or paste the resi number from your courier receipt"
                className={`w-full bg-cream-50 border rounded-xl px-3 py-2.5 font-body text-sm text-[#2C2A29] focus:outline-none focus:ring-2 ${
                  returnShipTrackingTouched && returnShipResi.hardError
                    ? 'border-red-300 focus:ring-red-200'
                    : returnShipTrackingTouched
                    ? 'border-emerald-300 focus:ring-emerald-200'
                    : 'border-cream-100 focus:ring-cream-200'
                }`}
                data-testid="return-tracking-input"
              />
              {returnShipTrackingTouched && returnShipResi.hardError && (
                <p className="text-xs font-body text-red-700 mt-1.5 leading-snug flex items-start gap-1.5" data-testid="return-tracking-error">
                  <AlertTriangle size={12} className="mt-0.5 flex-shrink-0" />
                  <span>{returnShipResi.hardError}</span>
                </p>
              )}
              {/* Escape hatch — only for PATTERN mismatches (not nonsense). */}
              {returnShipTrackingTouched && returnShipResi.errorKind === 'pattern' && (
                <label className="mt-2 flex items-start gap-2 text-xs font-body text-[#525252] bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 cursor-pointer hover:bg-amber-100 transition-all" data-testid="return-awb-override-label">
                  <input
                    type="checkbox"
                    checked={returnShipForm.return_awb_format_overridden}
                    onChange={e => setReturnShipForm(f => ({ ...f, return_awb_format_overridden: e.target.checked }))}
                    className="mt-0.5 flex-shrink-0 accent-amber-700"
                    data-testid="return-awb-override-checkbox"
                  />
                  <span className="leading-snug">I confirm this AWB is correct as printed on my courier receipt. <span className="text-amber-800">Admin will spot-check before refund.</span></span>
                </label>
              )}
            </div>
            <div>
              <label className="block text-xs font-body font-medium text-[#78716C] mb-1">Shipment proof photo *</label>
              <p className="text-xs font-body text-[#A89F91] mb-2 leading-relaxed">
                Upload a photo of the return parcel showing the resi attached to it, or a photo of the shipment receipt (nota pengiriman).
              </p>
              {returnShipForm.return_shipment_proof_url ? (
                <div className="relative w-full rounded-xl overflow-hidden border border-emerald-200 bg-emerald-50" data-testid="return-ship-proof-preview">
                  <img
                    src={returnShipForm.return_shipment_proof_url.startsWith('http') ? returnShipForm.return_shipment_proof_url : `${process.env.REACT_APP_BACKEND_URL}${returnShipForm.return_shipment_proof_url}`}
                    alt="Return shipment proof"
                    className="w-full max-h-64 object-contain bg-cream-50"
                  />
                  <button
                    type="button"
                    onClick={() => setReturnShipForm(f => ({ ...f, return_shipment_proof_url: '' }))}
                    className="absolute top-2 right-2 bg-white/95 hover:bg-white text-red-600 rounded-full p-1.5 shadow-sm transition-all"
                    data-testid="return-ship-proof-remove-btn"
                    title="Remove and re-upload"
                  >
                    <XCircle size={16} />
                  </button>
                </div>
              ) : (
                <label
                  htmlFor="return-ship-proof-file"
                  className={`w-full flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed py-6 px-3 transition-all cursor-pointer ${
                    returnShipProofUploading
                      ? 'border-orange-200 bg-orange-50 text-orange-700'
                      : 'border-cream-200 bg-cream-50 hover:border-orange-300 hover:bg-orange-50 text-[#78716C] hover:text-orange-700'
                  }`}
                  data-testid="return-ship-proof-upload-label"
                >
                  {returnShipProofUploading ? (
                    <>
                      <Loader2 size={20} className="animate-spin" />
                      <span className="text-sm font-body">Compressing & uploading...</span>
                    </>
                  ) : (
                    <>
                      <Camera size={20} />
                      <span className="text-sm font-body font-medium">Tap to upload photo</span>
                      <span className="text-xs font-body text-[#A89F91]">JPG / PNG · auto-compressed to ≤ 2 MB</span>
                    </>
                  )}
                  <input
                    id="return-ship-proof-file"
                    type="file"
                    accept="image/*"
                    capture="environment"
                    className="hidden"
                    disabled={returnShipProofUploading}
                    onChange={e => handleReturnShipProofUpload(e.target.files?.[0])}
                    data-testid="return-ship-proof-file-input"
                  />
                </label>
              )}
            </div>
            <button
              onClick={handleSubmitReturnShip}
              disabled={submitting || returnShipProofUploading || !returnShipForm.return_shipment_proof_url || !returnShipForm.return_courier.trim() || returnShipBlocked}
              className="w-full bg-orange-100 text-orange-800 rounded-full px-6 py-3 font-body font-semibold hover:bg-orange-200 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              data-testid="return-ship-submit-btn"
            >
              {submitting ? <><Loader2 size={16} className="animate-spin" /> Recording...</> : 'Confirm Return Shipment'}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Seller Dispute Dialog */}
      <Dialog open={sellerDisputeDialog.open} onOpenChange={(v) => { if (!v) setSellerDisputeDialog({ open: false, order: null, returnId: '' }); }}>
        <DialogContent className="rounded-3xl border-cream-100 bg-white max-w-lg mx-4 max-h-[90vh] overflow-y-auto" data-testid="seller-dispute-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-[#2C2A29]">Dispute Return</DialogTitle>
            <DialogDescription className="font-body text-[#78716C]">
              Raise an issue with the returned item. Admin will review both sides.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            {/* Reason */}
            <div>
              <label className="block text-xs font-body font-medium text-[#78716C] mb-2">What's the issue? *</label>
              <div className="space-y-2">
                {SELLER_DISPUTE_REASONS.map(r => (
                  <button key={r.value} type="button"
                    onClick={() => setSellerDisputeForm(f => ({ ...f, reason: r.value }))}
                    className={`w-full text-left px-4 py-3 rounded-xl border text-sm font-body transition-all ${
                      sellerDisputeForm.reason === r.value ? 'border-red-300 bg-red-50 font-medium text-[#2C2A29]' : 'border-cream-100 text-[#78716C] hover:border-red-200'
                    }`}
                    data-testid={`dispute-reason-${r.value}`}>
                    {r.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Details */}
            <div>
              <label className="block text-xs font-body font-medium text-[#78716C] mb-1">Additional details</label>
              <textarea value={sellerDisputeForm.reason_detail}
                onChange={e => setSellerDisputeForm(f => ({ ...f, reason_detail: e.target.value }))}
                placeholder="Describe the issue..."
                className="w-full bg-cream-50 border border-cream-100 rounded-xl px-3 py-2.5 font-body text-sm text-[#2C2A29] focus:outline-none focus:ring-2 focus:ring-red-200 focus:border-transparent resize-none"
                rows={2} data-testid="dispute-detail-input" />
            </div>

            {/* Photos: 4 slots, 1 mandatory */}
            <div>
              <label className="block text-xs font-body font-medium text-[#78716C] mb-1">Photos * <span className="text-[#A89F91] font-normal">(1 required, up to 4)</span></label>
              <div className="flex flex-wrap gap-2">
                {sellerDisputeForm.photo_urls.map((url, i) => (
                  <div key={i} className="relative w-16 h-16">
                    <img src={url} alt="" className="w-full h-full rounded-lg object-cover" />
                    <button type="button" onClick={() => setSellerDisputeForm(f => ({ ...f, photo_urls: f.photo_urls.filter((_, j) => j !== i) }))}
                      className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white rounded-full flex items-center justify-center text-[8px]">x</button>
                  </div>
                ))}
                {sellerDisputeForm.photo_urls.length < 4 && (
                  <label className={`w-16 h-16 border-2 border-dashed rounded-lg flex items-center justify-center cursor-pointer transition-all ${
                    sellerDisputeForm.photo_urls.length === 0 ? 'border-red-300 bg-red-50/30' : 'border-cream-200 hover:border-red-300'
                  }`}>
                    <Camera size={18} className="text-[#A89F91]" />
                    <input type="file" accept="image/*" className="hidden" disabled={returnUploading}
                      onChange={async (e) => {
                        if (!e.target.files[0]) return;
                        setReturnUploading(true);
                        try {
                          const formData = new FormData();
                          formData.append('file', e.target.files[0]);
                          const res = await axios.post(`${API}/upload-evidence`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
                          const url = `${process.env.REACT_APP_BACKEND_URL}${res.data.url}`;
                          setSellerDisputeForm(f => ({ ...f, photo_urls: [...f.photo_urls, url] }));
                          toast.success('Photo uploaded');
                        } catch (err) { toast.error('Failed to upload photo'); }
                        finally { setReturnUploading(false); }
                      }}
                      data-testid="dispute-photo-input" />
                  </label>
                )}
              </div>
            </div>

            <div className="flex gap-3">
              <button onClick={() => setSellerDisputeDialog({ open: false, order: null, returnId: '' })}
                className="flex-1 border border-cream-100 text-[#2C2A29] rounded-full px-4 py-2.5 font-body font-semibold hover:bg-cream-50 transition-all">Cancel</button>
              <button onClick={handleSubmitSellerDispute} disabled={submitting || returnUploading}
                className="flex-1 bg-red-100 text-red-800 rounded-full px-4 py-2.5 font-body font-semibold hover:bg-red-200 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="dispute-submit-btn">
                {submitting ? <><Loader2 size={14} className="animate-spin" /> Submitting...</> : 'Submit Dispute'}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reusable Address form (same one used on /profile + /cart). Opened from the
          Book Shipment dialog when the seller needs to add or update their pickup
          address — which is also their shipping-to address for buying. The
          kecamatan is LOCKED to whatever was snapshotted on the order at sale
          time so that the buyer's paid shipping fee always matches the booking
          cost (Option D). Seller can change kecamatan from profile settings;
          that affects future listings only. */}
      <AddressFormDialog
        open={addrDialogOpen}
        onOpenChange={setAddrDialogOpen}
        addressForm={addrForm}
        setAddressForm={setAddrForm}
        onSave={handleAddressSave}
        lockKecamatan={!!shipDialog}
        lockedKecamatanLabel={shipDialog?.shipper_subdistrict_label || addrForm.subdistrict_label}
      />
    </div>
  );
}
